import { Canvas, ThreeEvent, useFrame, useThree } from '@react-three/fiber';
import type { ObjectMap } from '@react-three/fiber';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  BoxGeometry,
  Box3,
  BufferGeometry,
  CanvasTexture,
  Color,
  CircleGeometry,
  DoubleSide,
  DynamicDrawUsage,
  ExtrudeGeometry,
  Float32BufferAttribute,
  Group,
  LoadingManager,
  Object3D,
  RepeatWrapping,
  Shape,
  TorusGeometry,
  Uint32BufferAttribute,
  Vector3,
} from 'three';
import type { InstancedMesh, Material, Mesh } from 'three';
import Stats from 'three/examples/jsm/libs/stats.module.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader';
import { clone } from 'three/examples/jsm/utils/SkeletonUtils';

import {
  ASSET_PATHS,
  BELT_CHARACTERS,
  BeltType,
  Building,
  BuildingType,
  FAST_BELT_CHARACTERS,
  GAME_CONFIG,
  Item,
  ItemType,
  ITEMS,
  PlayerFacing,
  Position,
} from '../constants';
import { useMapState, useUIState } from '../contexts/GameStateManager';
import { getGameEngine } from '../GameEngine';
import { findBuildingByCharacter, getBuildingPositions } from '../GameState';
import { InputManager } from '../InputManager';

const TILE_RADIUS = 32;
const TILE_SPACING = 1.08;
const TERRAIN_TILE_SCALE = TILE_SPACING * 0.985;
const TERRAIN_HEIGHT = 0.18;
const BUILDING_HEIGHT = 0.5;
const ASSEMBLER_TILE_SIZE = 3;
const ROCKET_SILO_TILE_SIZE = 5;
const ASSEMBLER_BASE_HEIGHT = BUILDING_HEIGHT - 0.1;
const BELT_BASE_HEIGHT = TERRAIN_HEIGHT - 0.1;
const ITEM_MODEL_BASE_HEIGHT = BELT_BASE_HEIGHT + 0.28;
const BELT_ITEM_STACK_SPACING = 0.1;
const GENERIC_BUILDING_BASE_HEIGHT = BUILDING_HEIGHT - 0.1;
const RESOURCE_NODE_BASE_HEIGHT = TERRAIN_HEIGHT - 0.05;
const PLAYER_MODEL_BASE_HEIGHT = TERRAIN_HEIGHT + 0.02;
const PREVIEW_OVERLAY_HEIGHT = BUILDING_HEIGHT - 0.05;
const PREVIEW_MIN_OPACITY = 0.4;
const PREVIEW_MAX_OPACITY = 0.6;
const PREVIEW_PULSE_DURATION = 2;

const DEFAULT_BELT_ITEM_VERTICAL_OFFSET = 0.16;

const BELT_ITEM_VERTICAL_OFFSET_OVERRIDES: Partial<Record<ItemType, number>> = {
  'iron-plate': 0.16,
  'copper-plate': 0.16,
};

const getBeltItemVerticalOffset = (itemType: ItemType): number =>
  BELT_ITEM_VERTICAL_OFFSET_OVERRIDES[itemType] ??
  DEFAULT_BELT_ITEM_VERTICAL_OFFSET;

const POWER_ALERT_COLOR = 0xffd86b;
const POWER_ALERT_EMISSIVE = 0xffb347;
const POWER_ALERT_HALO_COLOR = 0xfff3a1;
const POWER_ALERT_GLOW_COLOR = 0xfff3a1;
const POWER_ALERT_BASE_HEIGHT = 1.2;
const POWER_ALERT_PULSE_PERIOD = 1.2;
const POWER_ALERT_SCALE_FACTOR = 0.35;
const POWER_ALERT_VERTICAL_OFFSET = 0.22;

const POWER_ALERT_HEIGHT_OVERRIDES: Partial<Record<BuildingType, number>> = {
  [BuildingType.ASSEMBLER]: 2.2,
  [BuildingType.FACTORY]: 2.8,
  [BuildingType.RESEARCH_FACILITY]: 2.6,
  [BuildingType.ROCKET_SILO]: 5.2,
  [BuildingType.GENERATOR]: 2.1,
  [BuildingType.WIND_TURBINE]: 2.4,
  [BuildingType.SOLAR_PANEL]: 1.6,
};

const BUTTERFLY_COUNT = 8;
const BUTTERFLY_CHUNK_SIZE = 24;
const BUTTERFLY_BOB_AMPLITUDE = 0.2;

const hashChunkCoordinates = (chunkX: number, chunkY: number): number => {
  const x = chunkX | 0;
  const y = chunkY | 0;
  let hash = Math.imul(x ^ 0x9e3779b9, 0x85ebca6b);
  hash ^= Math.imul(y ^ 0xc2b2ae35, 0x27d4eb2f);
  hash ^= hash >>> 15;
  return hash >>> 0;
};

const createSeededRandom = (seed: number) => {
  let t = seed;

  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
};

const BUILDING_MODEL_TYPE_MAP: Partial<
  Record<BuildingType, BuildingModelType>
> = {
  [BuildingType.MINER]: 'basicDrill',
  [BuildingType.DRILL]: 'advancedDrill',
  [BuildingType.PRECISION_DRILL]: 'advancedDrill',
  [BuildingType.SAND_DREDGER]: 'sandMiner',
  [BuildingType.RESEARCH_FACILITY]: 'researchStation',
  [BuildingType.GENERATOR]: 'generator',
  [BuildingType.WIND_TURBINE]: 'generator',
  [BuildingType.SOLAR_PANEL]: 'solarPanel',
  [BuildingType.SPLITTER]: 'splitter',
  [BuildingType.UNDERGROUND_BELT]: 'undergroundBelt',
  [BuildingType.FACTORY]: 'factory',
  [BuildingType.IRON_CHEST]: 'ironChest',
  [BuildingType.STEEL_CHEST]: 'steelChest',
  [BuildingType.WAREHOUSE]: 'steelChest',
  [BuildingType.ROCKET_SILO]: 'rocketSilo',
};

const ITEMS_BY_TYPE: Record<ItemType, Item> = Object.values(ITEMS).reduce(
  (acc, item) => {
    acc[item.type] = item;
    return acc;
  },
  {} as Record<ItemType, Item>
);

const MODEL_LABELS_BY_FILENAME = new Map<string, string>([
  ['assembler.glb', 'Assembler'],
  ['basic_drill.glb', 'Basic Drill'],
  ['advanced_drill.glb', 'Advanced Drill'],
  ['sand_miner.glb', 'Sand Miner'],
  ['generator.glb', 'Generator'],
  ['solar_panel.glb', 'Solar Panel'],
  ['research_station.glb', 'Research Lab'],
  ['splitter.glb', 'Splitter'],
  ['underground_belt.glb', 'Underground Belt'],
  ['factory.glb', 'Factory'],
  ['rocket_silo.glb', 'Rocket Silo'],
  ['iron_chest.glb', 'Iron Chest'],
  ['steel_chest.glb', 'Steel Chest'],
  ['iron_deposit.glb', 'Iron Deposit'],
  ['copper_deposit.glb', 'Copper Deposit'],
  ['iron_ore.glb', 'Iron Ore'],
  ['copper_ore.glb', 'Copper Ore'],
  ['gear.glb', 'Gear'],
  ['iron_plate.glb', 'Iron Plate'],
  ['copper_plate.glb', 'Copper Plate'],
  ['basic_circuit.glb', 'Basic Circuit'],
  ['advanced_circuit.glb', 'Advanced Circuit'],
  ['transistor.glb', 'Transistor'],
  ['sand.glb', 'Sand'],
  ['player.glb', 'Player'],
]);

const resolveAssetUrl = (relativePath: string): string => {
  const publicUrl = process.env.PUBLIC_URL ?? '';
  if (relativePath.startsWith('/')) {
    return `${publicUrl}${relativePath}`;
  }

  const needsSlash = publicUrl !== '' && !publicUrl.endsWith('/');
  const separator = needsSlash ? '/' : '';
  return `${publicUrl}${separator}${relativePath}`;
};

const GLTF_MODEL_RELATIVE_PATH_ENTRIES = [
  ['assembler', ASSET_PATHS.ASSEMBLER_MODEL],
  ['basicDrill', ASSET_PATHS.BASIC_DRILL_MODEL],
  ['advancedDrill', ASSET_PATHS.ADVANCED_DRILL_MODEL],
  ['sandMiner', ASSET_PATHS.SAND_MINER_MODEL],
  ['generator', ASSET_PATHS.GENERATOR_MODEL],
  ['solarPanel', ASSET_PATHS.SOLAR_PANEL_MODEL],
  ['researchStation', ASSET_PATHS.RESEARCH_STATION_MODEL],
  ['splitter', ASSET_PATHS.SPLITTER_MODEL],
  ['undergroundBelt', ASSET_PATHS.UNDERGROUND_BELT_MODEL],
  ['factory', ASSET_PATHS.FACTORY_MODEL],
  ['rocketSilo', ASSET_PATHS.ROCKET_SILO_MODEL],
  ['ironChest', ASSET_PATHS.IRON_CHEST_MODEL],
  ['steelChest', ASSET_PATHS.STEEL_CHEST_MODEL],
  ['ironDeposit', ASSET_PATHS.IRON_DEPOSIT_MODEL],
  ['copperDeposit', ASSET_PATHS.COPPER_DEPOSIT_MODEL],
  ['ironOre', ASSET_PATHS.IRON_ORE_MODEL],
  ['copperOre', ASSET_PATHS.COPPER_ORE_MODEL],
  ['gear', ASSET_PATHS.GEAR_MODEL],
  ['ironPlate', ASSET_PATHS.IRON_PLATE_MODEL],
  ['copperPlate', ASSET_PATHS.COPPER_PLATE_MODEL],
  ['basicCircuit', ASSET_PATHS.BASIC_CIRCUIT_MODEL],
  ['advancedCircuit', ASSET_PATHS.ADVANCED_CIRCUIT_MODEL],
  ['transistor', ASSET_PATHS.TRANSISTOR_MODEL],
  ['sand', ASSET_PATHS.SAND_MODEL],
  ['player', ASSET_PATHS.PLAYER_MODEL],
] as const;

type GltfModelKey = (typeof GLTF_MODEL_RELATIVE_PATH_ENTRIES)[number][0];

const GLTF_MODEL_RELATIVE_PATHS = new Map<GltfModelKey, string>(
  GLTF_MODEL_RELATIVE_PATH_ENTRIES
);

type LoadedGltf = GLTF & ObjectMap;

type LoadedGltfMap = Map<GltfModelKey, LoadedGltf>;

const createLoadedGltfMap = (
  gltfs: LoadedGltf[],
  entries: readonly (readonly [GltfModelKey, string])[]
): LoadedGltfMap => {
  if (gltfs.length !== entries.length) {
    throw new Error(
      `Expected ${entries.length} GLTF models, received ${gltfs.length}`
    );
  }

  return new Map(
    entries.map(([key], index) => {
      const gltf = gltfs[index];

      if (!gltf) {
        throw new Error(`Missing loaded GLTF model for key "${key}"`);
      }

      return [key, gltf];
    })
  );
};

const getRequiredGltf = (
  gltfs: LoadedGltfMap,
  key: GltfModelKey
): LoadedGltf => {
  const gltf = gltfs.get(key);

  if (!gltf) {
    throw new Error(`Missing loaded GLTF model for key "${key}"`);
  }

  return gltf;
};

interface TileInstance {
  key: string;
  position: [number, number, number];
  height: number;
  color: number;
  boardPosition: Position;
  biomeName: string;
  isEmpty: boolean;
  terrainChar: string | undefined;
  hasBuilding: boolean;
}

interface PreviewInstance {
  key: string;
  position: [number, number, number];
  boardPosition: Position;
  isInvalid: boolean;
}

interface BuildingPreviewInstance {
  key: string;
  position: [number, number, number];
  rotationY: number;
  type: BuildingModelType;
}

interface AssemblerPreviewInstance {
  key: string;
  position: [number, number, number];
}

interface AssemblerInstance {
  key: string;
  position: [number, number, number];
  boardPosition: Position;
}

interface AssemblerModelInstanceProps {
  baseScene: Group;
  position: [number, number, number];
  groundOffset: number;
}

interface BeltInstance {
  key: string;
  position: [number, number, number];
  rotationY: number;
  boardPosition: Position;
  isFast: boolean;
}

interface ItemModelInstanceProps {
  baseScene: Group;
  position: [number, number, number];
  groundOffset: number;
  rotationY: number;
}

interface ItemModelData {
  baseScene: Group;
  groundOffset: number;
}

interface BeltItemInstance {
  key: string;
  position: [number, number, number];
  rotationY: number;
  itemType: ItemType;
  boardPosition: Position;
}

type BuildingModelType =
  | 'basicDrill'
  | 'advancedDrill'
  | 'sandMiner'
  | 'generator'
  | 'solarPanel'
  | 'researchStation'
  | 'splitter'
  | 'undergroundBelt'
  | 'factory'
  | 'ironChest'
  | 'steelChest'
  | 'rocketSilo';

interface BuildingModelInstance {
  key: string;
  position: [number, number, number];
  rotationY: number;
  type: BuildingModelType;
  boardPosition: Position;
}

type ResourceDepositType = 'iron' | 'copper';

interface ResourceDepositInstance {
  key: string;
  position: [number, number, number];
  type: ResourceDepositType;
  boardPosition: Position;
}

interface PowerAlertInstance {
  key: string;
  position: [number, number, number];
  boardPosition: Position;
  pulseOffset: number;
}

type BoardPointerEventHandler = (
  event: ThreeEvent<PointerEvent>,
  position: Position
) => void;

interface QuickBeltDragState {
  readonly pointerId: number;
  readonly beltType: BeltType;
}

interface SceneContentProps {
  tiles: TileInstance[];
  terrainOffset: [number, number, number];
  previewTiles: PreviewInstance[];
  buildingPreview: BuildingPreviewInstance | null;
  assemblerPreview: AssemblerPreviewInstance | null;
  targetPosition: [number, number, number] | null;
  normalizedZoom: number;
  playerFacing: PlayerFacing;
  playerPos: Position;
  assemblers: AssemblerInstance[];
  belts: BeltInstance[];
  beltItems: BeltItemInstance[];
  buildingModels: BuildingModelInstance[];
  resourceDeposits: ResourceDepositInstance[];
  powerAlerts: PowerAlertInstance[];
  powerOverlayActive: boolean;
  loadedGltfs: LoadedGltfMap;
  onBoardPointerDown?: BoardPointerEventHandler;
  onBoardPointerUp?: BoardPointerEventHandler;
  onBoardPointerOver?: BoardPointerEventHandler;
  onBoardPointerOut?: BoardPointerEventHandler;
}

interface TerrainSceneData {
  readonly normalizedZoom: number;
  readonly origin: Position;
  readonly tiles: TileInstance[];
  readonly resourceDeposits: ResourceDepositInstance[];
}

interface ModelLoadingState {
  readonly isLoading: boolean;
  readonly itemsLoaded: number;
  readonly itemsTotal: number;
  readonly activeLabel: string | null;
  readonly progressPercentage: number;
}

const INITIAL_MODEL_LOADING_STATE: ModelLoadingState = {
  isLoading: false,
  itemsLoaded: 0,
  itemsTotal: 0,
  activeLabel: null,
  progressPercentage: 0,
};

const calculateModelLoadingProgress = (
  itemsLoaded: number,
  itemsTotal: number,
  hasCompleted: boolean
): number => {
  if (itemsTotal <= 0) {
    return hasCompleted ? 100 : 0;
  }

  if (hasCompleted) {
    return 100;
  }

  const normalizedProgress = Math.min(itemsLoaded / itemsTotal, 1);
  const scaledProgress = Math.min(normalizedProgress * 2, 0.99);

  return scaledProgress * 100;
};

const getModelLabelFromUrl = (url: string): string => {
  const sanitizedUrl = url.split('?')[0]?.split('#')[0] ?? url;
  const segments = sanitizedUrl.split('/');
  const fileName = segments[segments.length - 1] ?? sanitizedUrl;
  const mappedLabel = MODEL_LABELS_BY_FILENAME.get(fileName);
  if (mappedLabel) {
    return mappedLabel;
  }

  const baseName = fileName.replace(/\.[^.]+$/, '');
  if (!baseName) {
    return 'Asset';
  }

  return baseName
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const prepareModelData = (
  sourceScene: Group,
  desiredSpan: number
): ItemModelData => {
  const baseScene = sourceScene.clone(true);
  const boundingBox = new Box3().setFromObject(baseScene);
  const size = new Vector3();
  boundingBox.getSize(size);
  const maxHorizontalSpan = Math.max(size.x, size.z, 0.001);
  const scale = desiredSpan / maxHorizontalSpan;

  baseScene.scale.setScalar(scale);
  baseScene.position.set(0, 0, 0);

  const scaledBox = new Box3().setFromObject(baseScene);
  const groundOffset = -scaledBox.min.y;

  baseScene.traverse(object => {
    if ('isMesh' in object && object.isMesh) {
      object.castShadow = true;
      object.receiveShadow = true;
    }
  });

  return {
    baseScene,
    groundOffset,
  };
};

const createLightningBoltGeometry = (): ExtrudeGeometry => {
  const shape = new Shape();
  shape.moveTo(0.28, 1.05);
  shape.lineTo(-0.16, 0.18);
  shape.lineTo(0.14, 0.18);
  shape.lineTo(-0.42, -0.9);
  shape.lineTo(0.32, -0.1);
  shape.lineTo(0, -0.1);
  shape.lineTo(0.28, 1.05);

  const geometry = new ExtrudeGeometry(shape, {
    depth: 0.3,
    bevelEnabled: true,
    bevelThickness: 0.08,
    bevelSize: 0.06,
    bevelSegments: 2,
    steps: 1,
  });

  geometry.center();
  geometry.scale(0.55, 0.55, 0.55);

  return geometry;
};

interface PowerAlertResources {
  bolt: ExtrudeGeometry;
  halo: TorusGeometry;
  glow: CircleGeometry;
}

const createPowerAlertResources = (): PowerAlertResources => {
  const bolt = createLightningBoltGeometry();

  const halo = new TorusGeometry(0.58, 0.08, 20, 48);
  halo.rotateX(Math.PI / 2);

  const glow = new CircleGeometry(0.74, 36);
  glow.rotateX(-Math.PI / 2);
  glow.translate(0, -0.38, 0);

  return { bolt, halo, glow };
};

const getPowerAlertHeight = (
  building: Building,
  footprintWidth: number,
  footprintHeight: number
): number => {
  const overrideHeight = POWER_ALERT_HEIGHT_OVERRIDES[building.id];
  if (overrideHeight) {
    return Math.max(0, overrideHeight - POWER_ALERT_VERTICAL_OFFSET);
  }

  const footprintSpan = Math.max(footprintWidth, footprintHeight);
  return Math.max(
    0,
    POWER_ALERT_BASE_HEIGHT + footprintSpan * 0.65 - POWER_ALERT_VERTICAL_OFFSET
  );
};

const easeInOutSine = (t: number): number =>
  0.5 - 0.5 * Math.cos(Math.PI * Math.max(0, Math.min(1, t)));

const getFacingRotation = (facing: PlayerFacing): number => {
  switch (facing) {
    case 'left':
      return Math.PI / 2;
    case 'right':
      return -Math.PI / 2;
    case 'up':
      return 0;
    case 'down':
    default:
      return Math.PI;
  }
};

const getTileColor = (
  terrain: string | undefined,
  building: Building | null
): number | null => {
  if (building) {
    if (building.model) {
      return null;
    }

    switch (building.category) {
      case 'mining':
        return 0xffd166;
      case 'production':
        return 0x06d6a0;
      case 'logistics':
        return 0x2f343a;
      case 'power':
        return 0xef476f;
      default:
        return 0xe0fbfc;
    }
  }

  switch (terrain) {
    case GAME_CONFIG.GRASS_CHARACTER:
      return 0x3a5a40;
    case GAME_CONFIG.SAND_CHARACTER:
      return 0xc2b280;
    case GAME_CONFIG.WATER_CHARACTER:
      return 0x1b4965;
    case GAME_CONFIG.TREE_CHARACTER:
      return 0x386641;
    case GAME_CONFIG.ROCK_CHARACTER:
      return 0x6c757d;
    case GAME_CONFIG.MOUNTAIN_CHARACTER:
      return 0x4a4e69;
    case GAME_CONFIG.IRON_NODE_CHARACTER:
      return 0xb0c4de;
    case GAME_CONFIG.COPPER_NODE_CHARACTER:
      return 0xffb347;
    case GAME_CONFIG.BRIDGE_CHARACTER:
      return 0x9d4edd;
    case GAME_CONFIG.WALL_CHARACTER:
      return 0x343a40;
    case GAME_CONFIG.EMPTY_CHARACTER:
    default:
      return 0x1f2933;
  }
};

const TERRAIN_SEGMENTS = 6;
const MIN_SURFACE_GAP = 0.02;

interface TerrainProfile {
  readonly amplitude: number;
  readonly baseOffset: number;
  readonly frequency: number;
  readonly octaves: number;
  readonly lacunarity: number;
  readonly persistence: number;
}

interface TerrainColorPalette {
  readonly shadow: number;
  readonly base: number;
  readonly highlight: number;
}

type TerrainColorComponents = readonly [number, number, number];

interface TerrainColorPaletteComponents {
  readonly shadow: TerrainColorComponents;
  readonly base: TerrainColorComponents;
  readonly highlight: TerrainColorComponents;
}

interface BiomeTerrainProfileAdjustment {
  readonly amplitudeScale: number;
  readonly baseOffset: number;
  readonly frequencyScale: number;
  readonly persistenceScale: number;
}

const DEFAULT_TERRAIN_PROFILE: TerrainProfile = {
  amplitude: 0.14,
  baseOffset: 0,
  frequency: 0.65,
  octaves: 4,
  lacunarity: 2.1,
  persistence: 0.55,
};

const TERRAIN_PROFILE_OVERRIDES = new Map<string, TerrainProfile>([
  [
    GAME_CONFIG.GRASS_CHARACTER,
    {
      amplitude: 0.16,
      baseOffset: 0.02,
      frequency: 0.6,
      octaves: 4,
      lacunarity: 2.15,
      persistence: 0.58,
    },
  ],
  [
    GAME_CONFIG.SAND_CHARACTER,
    {
      amplitude: 0.1,
      baseOffset: -0.015,
      frequency: 0.85,
      octaves: 3,
      lacunarity: 2.2,
      persistence: 0.5,
    },
  ],
  [
    GAME_CONFIG.WATER_CHARACTER,
    {
      amplitude: 0.08,
      baseOffset: -0.14,
      frequency: 0.5,
      octaves: 4,
      lacunarity: 2,
      persistence: 0.7,
    },
  ],
  [
    GAME_CONFIG.MOUNTAIN_CHARACTER,
    {
      amplitude: 0.32,
      baseOffset: 0.15,
      frequency: 0.45,
      octaves: 5,
      lacunarity: 2.3,
      persistence: 0.65,
    },
  ],
  [
    GAME_CONFIG.ROCK_CHARACTER,
    {
      amplitude: 0.22,
      baseOffset: 0.06,
      frequency: 0.75,
      octaves: 4,
      lacunarity: 2.4,
      persistence: 0.6,
    },
  ],
  [
    GAME_CONFIG.TREE_CHARACTER,
    {
      amplitude: 0.16,
      baseOffset: 0.04,
      frequency: 0.78,
      octaves: 4,
      lacunarity: 2.1,
      persistence: 0.55,
    },
  ],
  [
    GAME_CONFIG.BRIDGE_CHARACTER,
    {
      amplitude: 0.04,
      baseOffset: 0,
      frequency: 1.05,
      octaves: 3,
      lacunarity: 2.05,
      persistence: 0.5,
    },
  ],
  [
    GAME_CONFIG.WALL_CHARACTER,
    {
      amplitude: 0.04,
      baseOffset: 0.05,
      frequency: 1.2,
      octaves: 2,
      lacunarity: 2,
      persistence: 0.45,
    },
  ],
  [
    GAME_CONFIG.IRON_NODE_CHARACTER,
    {
      amplitude: 0.14,
      baseOffset: 0.05,
      frequency: 0.7,
      octaves: 4,
      lacunarity: 2.05,
      persistence: 0.55,
    },
  ],
  [
    GAME_CONFIG.COPPER_NODE_CHARACTER,
    {
      amplitude: 0.14,
      baseOffset: 0.05,
      frequency: 0.7,
      octaves: 4,
      lacunarity: 2.05,
      persistence: 0.55,
    },
  ],
  [
    GAME_CONFIG.EMPTY_CHARACTER,
    {
      amplitude: 0.05,
      baseOffset: -0.07,
      frequency: 0.9,
      octaves: 2,
      lacunarity: 2,
      persistence: 0.45,
    },
  ],
]);

const DEFAULT_TERRAIN_PALETTE: TerrainColorPalette = {
  shadow: 0x16212c,
  base: 0x243342,
  highlight: 0x3e5162,
};

const TERRAIN_COLOR_PALETTES = new Map<string, TerrainColorPalette>([
  [
    GAME_CONFIG.GRASS_CHARACTER,
    {
      shadow: 0x17321f,
      base: 0x28563a,
      highlight: 0x4f8a58,
    },
  ],
  [
    GAME_CONFIG.SAND_CHARACTER,
    {
      shadow: 0x6b5a39,
      base: 0xaa9566,
      highlight: 0xd7c38a,
    },
  ],
  [
    GAME_CONFIG.WATER_CHARACTER,
    {
      shadow: 0x12364a,
      base: 0x1e5977,
      highlight: 0x4e9fbc,
    },
  ],
  [
    GAME_CONFIG.ROCK_CHARACTER,
    {
      shadow: 0x343942,
      base: 0x626a74,
      highlight: 0x97a0ab,
    },
  ],
  [
    GAME_CONFIG.MOUNTAIN_CHARACTER,
    {
      shadow: 0x2f333d,
      base: 0x565d69,
      highlight: 0x8d97a5,
    },
  ],
  [
    GAME_CONFIG.TREE_CHARACTER,
    {
      shadow: 0x183323,
      base: 0x2b5b38,
      highlight: 0x4e8b55,
    },
  ],
  [
    GAME_CONFIG.IRON_NODE_CHARACTER,
    {
      shadow: 0x4e5962,
      base: 0x8f9eaa,
      highlight: 0xd5e0e8,
    },
  ],
  [
    GAME_CONFIG.COPPER_NODE_CHARACTER,
    {
      shadow: 0x67412f,
      base: 0xb86c48,
      highlight: 0xf1a770,
    },
  ],
  [
    GAME_CONFIG.BRIDGE_CHARACTER,
    {
      shadow: 0x3c2f46,
      base: 0x665276,
      highlight: 0x8f7ea0,
    },
  ],
  [
    GAME_CONFIG.WALL_CHARACTER,
    {
      shadow: 0x21262e,
      base: 0x3a434e,
      highlight: 0x5d6875,
    },
  ],
  [
    GAME_CONFIG.EMPTY_CHARACTER,
    {
      shadow: 0x0d141b,
      base: 0x18232d,
      highlight: 0x314654,
    },
  ],
]);

const BIOME_TERRAIN_PROFILE_ADJUSTMENTS = new Map<
  string,
  BiomeTerrainProfileAdjustment
>([
  [
    'Plains',
    {
      amplitudeScale: 1,
      baseOffset: 0,
      frequencyScale: 1,
      persistenceScale: 1,
    },
  ],
  [
    'Forest',
    {
      amplitudeScale: 1.08,
      baseOffset: 0.01,
      frequencyScale: 0.92,
      persistenceScale: 1.04,
    },
  ],
  [
    'Desert',
    {
      amplitudeScale: 1.22,
      baseOffset: -0.006,
      frequencyScale: 0.78,
      persistenceScale: 0.95,
    },
  ],
  [
    'Mountains',
    {
      amplitudeScale: 1.28,
      baseOffset: 0.018,
      frequencyScale: 1.18,
      persistenceScale: 1.08,
    },
  ],
  [
    'Lake',
    {
      amplitudeScale: 0.9,
      baseOffset: -0.02,
      frequencyScale: 0.82,
      persistenceScale: 0.98,
    },
  ],
  [
    'Wetlands',
    {
      amplitudeScale: 1.05,
      baseOffset: -0.014,
      frequencyScale: 0.88,
      persistenceScale: 1.02,
    },
  ],
  [
    'Tundra',
    {
      amplitudeScale: 0.72,
      baseOffset: -0.01,
      frequencyScale: 1.28,
      persistenceScale: 0.9,
    },
  ],
  [
    'Badlands',
    {
      amplitudeScale: 1.18,
      baseOffset: 0.01,
      frequencyScale: 1.08,
      persistenceScale: 1.05,
    },
  ],
]);

const BIOME_TERRAIN_PALETTES = new Map<string, TerrainColorPalette>([
  [
    'Plains:empty',
    {
      shadow: 0x142028,
      base: 0x21303a,
      highlight: 0x3f5762,
    },
  ],
  [
    'Forest:empty',
    {
      shadow: 0x102018,
      base: 0x1b3528,
      highlight: 0x345a45,
    },
  ],
  [
    'Forest:grass',
    {
      shadow: 0x14311e,
      base: 0x286240,
      highlight: 0x5a9b63,
    },
  ],
  [
    'Desert:empty',
    {
      shadow: 0x2c2418,
      base: 0x4a3b26,
      highlight: 0x7f6542,
    },
  ],
  [
    'Desert:sand',
    {
      shadow: 0x7a5f35,
      base: 0xc7a86a,
      highlight: 0xf0d294,
    },
  ],
  [
    'Mountains:empty',
    {
      shadow: 0x182028,
      base: 0x273342,
      highlight: 0x415364,
    },
  ],
  [
    'Mountains:mountain',
    {
      shadow: 0x313742,
      base: 0x5b6471,
      highlight: 0xa2aebb,
    },
  ],
  [
    'Lake:empty',
    {
      shadow: 0x10252d,
      base: 0x18414d,
      highlight: 0x2e6d7b,
    },
  ],
  [
    'Lake:water',
    {
      shadow: 0x0f3d4e,
      base: 0x1d6f8b,
      highlight: 0x72c4d8,
    },
  ],
  [
    'Wetlands:empty',
    {
      shadow: 0x12261f,
      base: 0x234239,
      highlight: 0x46685c,
    },
  ],
  [
    'Wetlands:grass',
    {
      shadow: 0x17392c,
      base: 0x2f6a52,
      highlight: 0x68a17f,
    },
  ],
  [
    'Wetlands:water',
    {
      shadow: 0x133f42,
      base: 0x1f6d72,
      highlight: 0x61b6ac,
    },
  ],
  [
    'Tundra:empty',
    {
      shadow: 0x182026,
      base: 0x293744,
      highlight: 0x526677,
    },
  ],
  [
    'Tundra:grass',
    {
      shadow: 0x2a3340,
      base: 0x4b6070,
      highlight: 0x91a6b6,
    },
  ],
  [
    'Tundra:mountain',
    {
      shadow: 0x2f3641,
      base: 0x5d6875,
      highlight: 0xb0bbc8,
    },
  ],
  [
    'Badlands:empty',
    {
      shadow: 0x2a1914,
      base: 0x4a2c20,
      highlight: 0x845342,
    },
  ],
  [
    'Badlands:sand',
    {
      shadow: 0x6e3f2f,
      base: 0xb7674d,
      highlight: 0xeb9a74,
    },
  ],
  [
    'Badlands:rock',
    {
      shadow: 0x4a2c24,
      base: 0x825144,
      highlight: 0xbc7d68,
    },
  ],
]);

const BUILDING_TERRAIN_PALETTE: TerrainColorPalette = {
  shadow: 0x0d141b,
  base: 0x1c2833,
  highlight: 0x334452,
};

const toTerrainColorComponents = (color: number): TerrainColorComponents => {
  return [
    ((color >> 16) & 0xff) / 255,
    ((color >> 8) & 0xff) / 255,
    (color & 0xff) / 255,
  ];
};

const toTerrainColorPaletteComponents = (
  palette: TerrainColorPalette
): TerrainColorPaletteComponents => {
  return {
    shadow: toTerrainColorComponents(palette.shadow),
    base: toTerrainColorComponents(palette.base),
    highlight: toTerrainColorComponents(palette.highlight),
  };
};

const DEFAULT_TERRAIN_PALETTE_COMPONENTS = toTerrainColorPaletteComponents(
  DEFAULT_TERRAIN_PALETTE
);

const BUILDING_TERRAIN_PALETTE_COMPONENTS = toTerrainColorPaletteComponents(
  BUILDING_TERRAIN_PALETTE
);

const TERRAIN_COLOR_PALETTE_COMPONENTS = new Map<
  string,
  TerrainColorPaletteComponents
>(
  Array.from(TERRAIN_COLOR_PALETTES.entries(), ([key, palette]) => [
    key,
    toTerrainColorPaletteComponents(palette),
  ])
);

const BIOME_TERRAIN_PALETTE_COMPONENTS = new Map<
  string,
  TerrainColorPaletteComponents
>(
  Array.from(BIOME_TERRAIN_PALETTES.entries(), ([key, palette]) => [
    key,
    toTerrainColorPaletteComponents(palette),
  ])
);

const lerp = (start: number, end: number, t: number): number =>
  start + (end - start) * t;

const smoothstep = (value: number): number => value * value * (3 - 2 * value);

const pseudoRandom2D = (x: number, y: number): number => {
  const dot = x * 127.1 + y * 311.7;
  const sinValue = Math.sin(dot) * 43758.5453;
  return sinValue - Math.floor(sinValue);
};

const valueNoise2D = (x: number, y: number): number => {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const x1 = x0 + 1;
  const y1 = y0 + 1;

  const sx = smoothstep(x - x0);
  const sy = smoothstep(y - y0);

  const n0 = pseudoRandom2D(x0, y0);
  const n1 = pseudoRandom2D(x1, y0);
  const ix0 = lerp(n0, n1, sx);

  const n2 = pseudoRandom2D(x0, y1);
  const n3 = pseudoRandom2D(x1, y1);
  const ix1 = lerp(n2, n3, sx);

  return lerp(ix0, ix1, sy);
};

const fractalNoise2D = (
  x: number,
  y: number,
  octaves: number,
  lacunarity: number,
  persistence: number
): number => {
  let amplitude = 1;
  let frequency = 1;
  let total = 0;
  let max = 0;

  for (let i = 0; i < Math.max(1, Math.floor(octaves)); i += 1) {
    total += valueNoise2D(x * frequency, y * frequency) * amplitude;
    max += amplitude;
    amplitude *= persistence;
    frequency *= lacunarity;
  }

  if (max === 0) {
    return 0;
  }

  return total / max;
};

const getTileTerrainProfile = (
  terrain: string | undefined,
  tileHeight: number,
  hasBuilding: boolean,
  isEmpty: boolean,
  biomeName: string
): TerrainProfile => {
  if (hasBuilding) {
    return {
      amplitude: Math.min(0.08, tileHeight * 0.12),
      baseOffset: 0,
      frequency: 1.1,
      octaves: 2,
      lacunarity: 2,
      persistence: 0.4,
    };
  }

  if (isEmpty) {
    return {
      amplitude: Math.min(0.06, tileHeight * 0.1),
      baseOffset: -tileHeight * 0.5,
      frequency: 0.9,
      octaves: 2,
      lacunarity: 2,
      persistence: 0.45,
    };
  }

  const profile = terrain
    ? (TERRAIN_PROFILE_OVERRIDES.get(terrain) ?? DEFAULT_TERRAIN_PROFILE)
    : DEFAULT_TERRAIN_PROFILE;

  const biomeAdjustment =
    BIOME_TERRAIN_PROFILE_ADJUSTMENTS.get(biomeName) ??
    BIOME_TERRAIN_PROFILE_ADJUSTMENTS.get('Plains');

  if (!biomeAdjustment) {
    return profile;
  }

  return {
    amplitude: profile.amplitude * biomeAdjustment.amplitudeScale,
    baseOffset: profile.baseOffset + biomeAdjustment.baseOffset,
    frequency: profile.frequency * biomeAdjustment.frequencyScale,
    octaves: profile.octaves,
    lacunarity: profile.lacunarity,
    persistence: profile.persistence * biomeAdjustment.persistenceScale,
  };
};

const getTerrainColorPaletteComponents = (
  terrain: string | undefined,
  biomeName: string,
  hasBuilding: boolean
): TerrainColorPaletteComponents => {
  if (hasBuilding) {
    return BUILDING_TERRAIN_PALETTE_COMPONENTS;
  }

  const terrainKey =
    terrain === GAME_CONFIG.GRASS_CHARACTER
      ? 'grass'
      : terrain === GAME_CONFIG.SAND_CHARACTER
        ? 'sand'
        : terrain === GAME_CONFIG.WATER_CHARACTER
          ? 'water'
          : terrain === GAME_CONFIG.ROCK_CHARACTER
            ? 'rock'
            : terrain === GAME_CONFIG.MOUNTAIN_CHARACTER
              ? 'mountain'
              : terrain === GAME_CONFIG.EMPTY_CHARACTER || !terrain
                ? 'empty'
                : null;

  if (terrainKey) {
    const biomePalette = BIOME_TERRAIN_PALETTE_COMPONENTS.get(
      `${biomeName}:${terrainKey}`
    );
    if (biomePalette) {
      return biomePalette;
    }
  }

  if (!terrain) {
    return DEFAULT_TERRAIN_PALETTE_COMPONENTS;
  }

  return (
    TERRAIN_COLOR_PALETTE_COMPONENTS.get(terrain) ??
    DEFAULT_TERRAIN_PALETTE_COMPONENTS
  );
};

const GroundBackdrop: React.FC = () => {
  const groundTexture = useMemo(() => {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;

    const context = canvas.getContext('2d');
    if (!context) {
      return null;
    }

    context.fillStyle = '#111a22';
    context.fillRect(0, 0, canvas.width, canvas.height);

    context.strokeStyle = 'rgba(88, 132, 156, 0.08)';
    context.lineWidth = 1;
    for (let offset = 0; offset <= canvas.width; offset += 64) {
      context.beginPath();
      context.moveTo(offset, 0);
      context.lineTo(offset, canvas.height);
      context.stroke();

      context.beginPath();
      context.moveTo(0, offset);
      context.lineTo(canvas.width, offset);
      context.stroke();
    }

    context.strokeStyle = 'rgba(160, 214, 230, 0.03)';
    for (let offset = 0; offset <= canvas.width; offset += 16) {
      context.beginPath();
      context.moveTo(offset, 0);
      context.lineTo(offset, canvas.height);
      context.stroke();

      context.beginPath();
      context.moveTo(0, offset);
      context.lineTo(canvas.width, offset);
      context.stroke();
    }

    for (let index = 0; index < 220; index += 1) {
      const x = Math.random() * canvas.width;
      const y = Math.random() * canvas.height;
      const size = 8 + Math.random() * 22;
      context.fillStyle =
        index % 5 === 0
          ? 'rgba(120, 174, 188, 0.06)'
          : 'rgba(18, 30, 38, 0.18)';
      context.fillRect(x, y, size, size * (0.5 + Math.random() * 1.4));
    }

    context.strokeStyle = 'rgba(218, 189, 120, 0.07)';
    context.lineWidth = 2;
    for (let offset = -canvas.height; offset < canvas.width; offset += 96) {
      context.beginPath();
      context.moveTo(offset, 0);
      context.lineTo(offset + canvas.height, canvas.height);
      context.stroke();
    }

    const texture = new CanvasTexture(canvas);
    texture.wrapS = RepeatWrapping;
    texture.wrapT = RepeatWrapping;
    texture.repeat.set(7, 7);
    return texture;
  }, []);

  useEffect(() => () => groundTexture?.dispose(), [groundTexture]);

  if (!groundTexture) {
    return null;
  }

  return (
    <mesh
      position={[0, -0.18, 0]}
      rotation={[-Math.PI / 2, 0, 0]}
      receiveShadow
    >
      <planeGeometry
        args={[
          TILE_SPACING * (TILE_RADIUS * 2 + 18),
          TILE_SPACING * (TILE_RADIUS * 2 + 18),
        ]}
      />
      <meshStandardMaterial
        color='#131d26'
        map={groundTexture}
        roughness={0.95}
        metalness={0.08}
      />
    </mesh>
  );
};

interface TerrainSurfaceProps {
  readonly tiles: TileInstance[];
  readonly onBoardPointerDown?: BoardPointerEventHandler | undefined;
  readonly onBoardPointerUp?: BoardPointerEventHandler | undefined;
  readonly onBoardPointerOver?: BoardPointerEventHandler | undefined;
  readonly onBoardPointerOut?: BoardPointerEventHandler | undefined;
}

const TERRAIN_VERTICES_PER_TILE =
  (TERRAIN_SEGMENTS + 1) * (TERRAIN_SEGMENTS + 1);
const TERRAIN_TRIANGLES_PER_TILE = TERRAIN_SEGMENTS * TERRAIN_SEGMENTS * 2;
const TERRAIN_INDICES_PER_TILE = TERRAIN_TRIANGLES_PER_TILE * 3;
const TERRAIN_CACHE_STEP = 4;
const TERRAIN_CACHE_MARGIN = 4;

const TERRAIN_VERTEX_LOCAL_X = new Float32Array(TERRAIN_VERTICES_PER_TILE);
const TERRAIN_VERTEX_LOCAL_Z = new Float32Array(TERRAIN_VERTICES_PER_TILE);
const TERRAIN_VERTEX_SAMPLE_OFFSET_X = new Float32Array(
  TERRAIN_VERTICES_PER_TILE
);
const TERRAIN_VERTEX_SAMPLE_OFFSET_Z = new Float32Array(
  TERRAIN_VERTICES_PER_TILE
);

for (let row = 0; row <= TERRAIN_SEGMENTS; row += 1) {
  for (let col = 0; col <= TERRAIN_SEGMENTS; col += 1) {
    const vertexIndex = row * (TERRAIN_SEGMENTS + 1) + col;
    const u = col / TERRAIN_SEGMENTS;
    const v = row / TERRAIN_SEGMENTS;
    const localX = (u - 0.5) * TERRAIN_TILE_SCALE;
    const localZ = (v - 0.5) * TERRAIN_TILE_SCALE;

    TERRAIN_VERTEX_LOCAL_X[vertexIndex] = localX;
    TERRAIN_VERTEX_LOCAL_Z[vertexIndex] = localZ;
    TERRAIN_VERTEX_SAMPLE_OFFSET_X[vertexIndex] = localX / TILE_SPACING;
    TERRAIN_VERTEX_SAMPLE_OFFSET_Z[vertexIndex] = localZ / TILE_SPACING;
  }
}

const TERRAIN_TILE_INDEX_TEMPLATE = new Uint16Array(TERRAIN_INDICES_PER_TILE);

{
  let indexIndex = 0;
  for (let row = 0; row < TERRAIN_SEGMENTS; row += 1) {
    for (let col = 0; col < TERRAIN_SEGMENTS; col += 1) {
      const topLeft = row * (TERRAIN_SEGMENTS + 1) + col;
      const topRight = topLeft + 1;
      const bottomLeft = topLeft + (TERRAIN_SEGMENTS + 1);
      const bottomRight = bottomLeft + 1;

      TERRAIN_TILE_INDEX_TEMPLATE[indexIndex] = topLeft;
      TERRAIN_TILE_INDEX_TEMPLATE[indexIndex + 1] = bottomLeft;
      TERRAIN_TILE_INDEX_TEMPLATE[indexIndex + 2] = topRight;
      TERRAIN_TILE_INDEX_TEMPLATE[indexIndex + 3] = topRight;
      TERRAIN_TILE_INDEX_TEMPLATE[indexIndex + 4] = bottomLeft;
      TERRAIN_TILE_INDEX_TEMPLATE[indexIndex + 5] = bottomRight;
      indexIndex += 6;
    }
  }
}

const TerrainSurface: React.FC<TerrainSurfaceProps> = React.memo(
  ({
    tiles,
    onBoardPointerDown,
    onBoardPointerUp,
    onBoardPointerOver,
    onBoardPointerOut,
  }) => {
    const hoveredPositionRef = useRef<Position | null>(null);

    const geometry = useMemo(() => {
      const bufferGeometry = new BufferGeometry();

      if (tiles.length === 0) {
        return bufferGeometry;
      }

      const vertexCount = tiles.length * TERRAIN_VERTICES_PER_TILE;
      const positions = new Float32Array(vertexCount * 3);
      const colors = new Float32Array(vertexCount * 3);
      const indices = new Uint32Array(tiles.length * TERRAIN_INDICES_PER_TILE);

      let positionIndex = 0;
      let colorIndex = 0;
      let indexIndex = 0;

      for (let tileIndex = 0; tileIndex < tiles.length; tileIndex += 1) {
        const tile = tiles[tileIndex];
        if (!tile) {
          continue;
        }
        const { boardPosition, height, terrainChar, hasBuilding, isEmpty } =
          tile;
        const palette = getTerrainColorPaletteComponents(
          terrainChar,
          tile.biomeName,
          hasBuilding
        );
        const terrainProfile = getTileTerrainProfile(
          terrainChar,
          height,
          hasBuilding,
          isEmpty,
          tile.biomeName
        );
        const tilePositionX = tile.position[0];
        const tilePositionY = tile.position[1];
        const tilePositionZ = tile.position[2];
        const boardPositionX = boardPosition.x;
        const boardPositionY = boardPosition.y;
        const halfHeight = height * 0.5;
        const surfaceBase = halfHeight + terrainProfile.baseOffset;
        const surfaceAmplitude = terrainProfile.amplitude;
        const minSurfaceHeight = -halfHeight + MIN_SURFACE_GAP;
        const maxSurfaceHeight = surfaceBase + Math.max(surfaceAmplitude, 0.02);
        const surfaceHeightDivisor = Math.max(height * 1.4, 0.001);
        const {
          frequency: surfaceFrequency,
          octaves: surfaceOctaves,
          lacunarity: surfaceLacunarity,
          persistence: surfacePersistence,
        } = terrainProfile;
        const [shadowR, shadowG, shadowB] = palette.shadow;
        const [baseR, baseG, baseB] = palette.base;
        const [highlightR, highlightG, highlightB] = palette.highlight;
        const tileVertexOffset = tileIndex * TERRAIN_VERTICES_PER_TILE;

        for (
          let vertexIndex = 0;
          vertexIndex < TERRAIN_VERTICES_PER_TILE;
          vertexIndex += 1
        ) {
          const localX = TERRAIN_VERTEX_LOCAL_X[vertexIndex] ?? 0;
          const localZ = TERRAIN_VERTEX_LOCAL_Z[vertexIndex] ?? 0;
          const sampleX =
            boardPositionX + (TERRAIN_VERTEX_SAMPLE_OFFSET_X[vertexIndex] ?? 0);
          const sampleZ =
            boardPositionY + (TERRAIN_VERTEX_SAMPLE_OFFSET_Z[vertexIndex] ?? 0);
          const terrainNoise =
            fractalNoise2D(
              sampleX * surfaceFrequency,
              sampleZ * surfaceFrequency,
              surfaceOctaves,
              surfaceLacunarity,
              surfacePersistence
            ) *
              2 -
            1;
          let surfaceHeight = surfaceBase + terrainNoise * surfaceAmplitude;
          if (surfaceHeight < minSurfaceHeight) {
            surfaceHeight = minSurfaceHeight;
          } else if (surfaceHeight > maxSurfaceHeight) {
            surfaceHeight = maxSurfaceHeight;
          }

          const broadNoise =
            fractalNoise2D(
              sampleX * 0.3 + 17.4,
              sampleZ * 0.3 - 9.7,
              3,
              2.2,
              0.5
            ) *
              2 -
            1;
          const fineNoise =
            fractalNoise2D(
              sampleX * 1.65 - 4.1,
              sampleZ * 1.65 + 7.2,
              2,
              2.35,
              0.45
            ) *
              2 -
            1;
          let elevation = (surfaceHeight + halfHeight) / surfaceHeightDivisor;
          if (elevation < 0) {
            elevation = 0;
          } else if (elevation > 1) {
            elevation = 1;
          }

          let baseBlend = 0.44 + broadNoise * 0.22 + fineNoise * 0.08;
          if (baseBlend < 0) {
            baseBlend = 0;
          } else if (baseBlend > 1) {
            baseBlend = 1;
          }

          let highlightBlend =
            0.16 + elevation * 0.58 + broadNoise * 0.16 - fineNoise * 0.04;
          if (highlightBlend < 0) {
            highlightBlend = 0;
          } else if (highlightBlend > 1) {
            highlightBlend = 1;
          }

          const baseMixR = shadowR + (baseR - shadowR) * baseBlend;
          const baseMixG = shadowG + (baseG - shadowG) * baseBlend;
          const baseMixB = shadowB + (baseB - shadowB) * baseBlend;

          positions[positionIndex] = tilePositionX + localX;
          positions[positionIndex + 1] = tilePositionY + surfaceHeight;
          positions[positionIndex + 2] = tilePositionZ + localZ;
          positionIndex += 3;

          colors[colorIndex] =
            baseMixR + (highlightR - baseMixR) * highlightBlend;
          colors[colorIndex + 1] =
            baseMixG + (highlightG - baseMixG) * highlightBlend;
          colors[colorIndex + 2] =
            baseMixB + (highlightB - baseMixB) * highlightBlend;
          colorIndex += 3;
        }

        for (
          let templateIndex = 0;
          templateIndex < TERRAIN_INDICES_PER_TILE;
          templateIndex += 1
        ) {
          indices[indexIndex + templateIndex] =
            (TERRAIN_TILE_INDEX_TEMPLATE[templateIndex] ?? 0) +
            tileVertexOffset;
        }
        indexIndex += TERRAIN_INDICES_PER_TILE;
      }

      bufferGeometry.setAttribute(
        'position',
        new Float32BufferAttribute(positions, 3)
      );
      bufferGeometry.setAttribute(
        'color',
        new Float32BufferAttribute(colors, 3)
      );
      bufferGeometry.setIndex(new Uint32BufferAttribute(indices, 1));
      bufferGeometry.computeVertexNormals();
      bufferGeometry.computeBoundingSphere();

      return bufferGeometry;
    }, [tiles]);

    useEffect(() => () => geometry.dispose(), [geometry]);

    const resolveTileFromEvent = useCallback(
      (event: ThreeEvent<PointerEvent>): TileInstance | null => {
        if (event.faceIndex == null || event.faceIndex < 0) {
          return null;
        }

        const tileIndex = Math.floor(
          event.faceIndex / TERRAIN_TRIANGLES_PER_TILE
        );
        return tiles[tileIndex] ?? null;
      },
      [tiles]
    );

    const handlePointerOver = useCallback(
      (event: ThreeEvent<PointerEvent>) => {
        event.stopPropagation();
        const tile = resolveTileFromEvent(event);
        if (!tile) {
          return;
        }

        const previous = hoveredPositionRef.current;
        if (
          previous &&
          previous.x === tile.boardPosition.x &&
          previous.y === tile.boardPosition.y
        ) {
          return;
        }

        hoveredPositionRef.current = tile.boardPosition;
        onBoardPointerOver?.(event, tile.boardPosition);
      },
      [onBoardPointerOver, resolveTileFromEvent]
    );

    const handlePointerOut = useCallback(
      (event: ThreeEvent<PointerEvent>) => {
        event.stopPropagation();
        const hoveredPosition = hoveredPositionRef.current;
        if (!hoveredPosition) {
          return;
        }

        hoveredPositionRef.current = null;
        onBoardPointerOut?.(event, hoveredPosition);
      },
      [onBoardPointerOut]
    );

    const handlePointerDown = useCallback(
      (event: ThreeEvent<PointerEvent>) => {
        event.stopPropagation();
        const tile = resolveTileFromEvent(event);
        if (tile) {
          onBoardPointerDown?.(event, tile.boardPosition);
        }
      },
      [onBoardPointerDown, resolveTileFromEvent]
    );

    const handlePointerUp = useCallback(
      (event: ThreeEvent<PointerEvent>) => {
        event.stopPropagation();
        const tile = resolveTileFromEvent(event);
        if (tile) {
          onBoardPointerUp?.(event, tile.boardPosition);
        }
      },
      [onBoardPointerUp, resolveTileFromEvent]
    );

    if (tiles.length === 0) {
      return null;
    }

    return (
      <mesh
        geometry={geometry}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerOver}
        onPointerOut={handlePointerOut}
        onPointerOver={handlePointerOver}
        onPointerUp={handlePointerUp}
      >
        <meshStandardMaterial
          color={0xffffff}
          vertexColors
          roughness={0.82}
          metalness={0.12}
          side={DoubleSide}
        />
      </mesh>
    );
  }
);

TerrainSurface.displayName = 'TerrainSurface';

const getBeltFacingFromCharacter = (character: string): PlayerFacing | null => {
  const beltEntry = Object.entries(BELT_CHARACTERS).find(([, value]) => {
    return value === character;
  });
  if (beltEntry) {
    return beltEntry[0] as PlayerFacing;
  }

  const fastEntry = Object.entries(FAST_BELT_CHARACTERS).find(([, value]) => {
    return value === character;
  });
  if (fastEntry) {
    return fastEntry[0] as PlayerFacing;
  }

  return null;
};

const getBeltRotation = (facing: PlayerFacing): number => {
  switch (facing) {
    case 'up':
      return -Math.PI / 2;
    case 'down':
      return Math.PI / 2;
    case 'left':
      return Math.PI;
    case 'right':
    default:
      return 0;
  }
};

const getFacingDelta = (facing: PlayerFacing): [number, number] => {
  switch (facing) {
    case 'up':
      return [0, -1];
    case 'down':
      return [0, 1];
    case 'left':
      return [-1, 0];
    case 'right':
    default:
      return [1, 0];
  }
};

const AssemblerModelInstance: React.FC<AssemblerModelInstanceProps> = ({
  baseScene,
  groundOffset,
  position,
}) => {
  const clonedScene = useMemo(() => clone(baseScene) as Group, [baseScene]);

  const finalPosition = useMemo<[number, number, number]>(() => {
    const [x, y, z] = position;
    return [x, y + groundOffset + ASSEMBLER_BASE_HEIGHT, z];
  }, [groundOffset, position]);

  return <primitive object={clonedScene} position={finalPosition} />;
};

interface InstancedBeltConveyorsProps {
  readonly belts: BeltInstance[];
  readonly onBoardPointerDown?: BoardPointerEventHandler | undefined;
  readonly onBoardPointerUp?: BoardPointerEventHandler | undefined;
  readonly onBoardPointerOver?: BoardPointerEventHandler | undefined;
}

const InstancedBeltConveyors: React.FC<InstancedBeltConveyorsProps> = ({
  belts,
  onBoardPointerDown,
  onBoardPointerUp,
  onBoardPointerOver,
}) => {
  const deckRef = useRef<InstancedMesh | null>(null);
  const leftRailRef = useRef<InstancedMesh | null>(null);
  const rightRailRef = useRef<InstancedMesh | null>(null);
  const stripeRef = useRef<InstancedMesh | null>(null);
  const hoveredPositionRef = useRef<Position | null>(null);
  const transform = useMemo(() => new Object3D(), []);

  const geometries = useMemo(() => {
    const deck = new BoxGeometry(
      TILE_SPACING * 0.92,
      0.04,
      TILE_SPACING * 0.56
    );
    deck.translate(0, BELT_BASE_HEIGHT + 0.02, 0);

    const leftRail = new BoxGeometry(
      TILE_SPACING * 0.98,
      0.12,
      TILE_SPACING * 0.08
    );
    leftRail.translate(0, BELT_BASE_HEIGHT + 0.075, -TILE_SPACING * 0.24);

    const rightRail = leftRail.clone();
    rightRail.translate(0, 0, TILE_SPACING * 0.48);

    const stripe = new BoxGeometry(
      TILE_SPACING * 0.32,
      0.016,
      TILE_SPACING * 0.14
    );
    stripe.translate(TILE_SPACING * 0.14, BELT_BASE_HEIGHT + 0.055, 0);

    return {
      deck,
      leftRail,
      rightRail,
      stripe,
    };
  }, []);

  useEffect(
    () => () => {
      geometries.deck.dispose();
      geometries.leftRail.dispose();
      geometries.rightRail.dispose();
      geometries.stripe.dispose();
    },
    [geometries]
  );

  useEffect(() => {
    const meshes = [
      deckRef.current,
      leftRailRef.current,
      rightRailRef.current,
      stripeRef.current,
    ];
    const standardDeckColor = new Color('#8f5a2b');
    const fastDeckColor = new Color('#c7782e');
    const standardRailColor = new Color('#4d2f22');
    const fastRailColor = new Color('#6c3f2a');
    const standardStripeColor = new Color('#d9b15b');
    const fastStripeColor = new Color('#ffe082');

    belts.forEach((belt, index) => {
      transform.position.set(
        belt.position[0],
        belt.position[1],
        belt.position[2]
      );
      transform.rotation.set(0, belt.rotationY, 0);
      transform.updateMatrix();

      const deckColor = belt.isFast ? fastDeckColor : standardDeckColor;
      const railColor = belt.isFast ? fastRailColor : standardRailColor;
      const stripeColor = belt.isFast ? fastStripeColor : standardStripeColor;

      deckRef.current?.setMatrixAt(index, transform.matrix);
      leftRailRef.current?.setMatrixAt(index, transform.matrix);
      rightRailRef.current?.setMatrixAt(index, transform.matrix);
      stripeRef.current?.setMatrixAt(index, transform.matrix);

      deckRef.current?.setColorAt(index, deckColor);
      leftRailRef.current?.setColorAt(index, railColor);
      rightRailRef.current?.setColorAt(index, railColor);
      stripeRef.current?.setColorAt(index, stripeColor);
    });

    meshes.forEach(mesh => {
      if (!mesh) {
        return;
      }
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) {
        mesh.instanceColor.needsUpdate = true;
      }
    });
  }, [belts, transform]);

  const resolveBelt = useCallback(
    (event: ThreeEvent<PointerEvent>): BeltInstance | null => {
      if (event.instanceId == null || event.instanceId < 0) {
        return null;
      }

      return belts[event.instanceId] ?? null;
    },
    [belts]
  );

  const handlePointerOver = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      event.stopPropagation();
      const belt = resolveBelt(event);
      if (!belt) {
        return;
      }

      const previous = hoveredPositionRef.current;
      if (
        previous &&
        previous.x === belt.boardPosition.x &&
        previous.y === belt.boardPosition.y
      ) {
        return;
      }

      hoveredPositionRef.current = belt.boardPosition;
      onBoardPointerOver?.(event, belt.boardPosition);
    },
    [onBoardPointerOver, resolveBelt]
  );

  const handlePointerDown = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      event.stopPropagation();
      const belt = resolveBelt(event);
      if (belt) {
        onBoardPointerDown?.(event, belt.boardPosition);
      }
    },
    [onBoardPointerDown, resolveBelt]
  );

  const handlePointerUp = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      event.stopPropagation();
      const belt = resolveBelt(event);
      if (belt) {
        onBoardPointerUp?.(event, belt.boardPosition);
      }
    },
    [onBoardPointerUp, resolveBelt]
  );

  if (belts.length === 0) {
    return null;
  }

  return (
    <group>
      <instancedMesh
        ref={deckRef}
        args={[geometries.deck, undefined, belts.length]}
        castShadow
        receiveShadow
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerOver}
        onPointerOver={handlePointerOver}
        onPointerUp={handlePointerUp}
      >
        <meshStandardMaterial vertexColors roughness={0.76} metalness={0.1} />
      </instancedMesh>
      <instancedMesh
        ref={leftRailRef}
        args={[geometries.leftRail, undefined, belts.length]}
        castShadow
        receiveShadow
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerOver}
        onPointerOver={handlePointerOver}
        onPointerUp={handlePointerUp}
      >
        <meshStandardMaterial vertexColors roughness={0.58} metalness={0.16} />
      </instancedMesh>
      <instancedMesh
        ref={rightRailRef}
        args={[geometries.rightRail, undefined, belts.length]}
        castShadow
        receiveShadow
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerOver}
        onPointerOver={handlePointerOver}
        onPointerUp={handlePointerUp}
      >
        <meshStandardMaterial vertexColors roughness={0.58} metalness={0.16} />
      </instancedMesh>
      <instancedMesh
        ref={stripeRef}
        args={[geometries.stripe, undefined, belts.length]}
        castShadow
        receiveShadow
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerOver}
        onPointerOver={handlePointerOver}
        onPointerUp={handlePointerUp}
      >
        <meshStandardMaterial vertexColors roughness={0.42} metalness={0.12} />
      </instancedMesh>
    </group>
  );
};

interface InstancedPowerAlertsProps {
  powerAlerts: PowerAlertInstance[];
  resources: PowerAlertResources;
  onBoardPointerDown?: BoardPointerEventHandler | undefined;
  onBoardPointerUp?: BoardPointerEventHandler | undefined;
  onBoardPointerOver?: BoardPointerEventHandler | undefined;
  onBoardPointerOut?: BoardPointerEventHandler | undefined;
}

const InstancedPowerAlerts: React.FC<InstancedPowerAlertsProps> = ({
  powerAlerts,
  resources,
  onBoardPointerDown,
  onBoardPointerUp,
  onBoardPointerOver,
  onBoardPointerOut,
}) => {
  const boltRef = useRef<InstancedMesh | null>(null);
  const haloRef = useRef<InstancedMesh | null>(null);
  const glowRef = useRef<InstancedMesh | null>(null);
  const hoveredPositionRef = useRef<Position | null>(null);
  const { camera } = useThree();
  const transform = useMemo(() => new Object3D(), []);
  const boltColor = useMemo(() => new Color(POWER_ALERT_COLOR), []);
  const haloColor = useMemo(() => new Color(POWER_ALERT_HALO_COLOR), []);
  const glowColor = useMemo(() => new Color(POWER_ALERT_GLOW_COLOR), []);
  const workingBoltColor = useMemo(() => new Color(), []);
  const workingHaloColor = useMemo(() => new Color(), []);
  const workingGlowColor = useMemo(() => new Color(), []);

  useEffect(() => {
    [boltRef.current, haloRef.current, glowRef.current].forEach(mesh => {
      if (!mesh) {
        return;
      }
      mesh.instanceMatrix.setUsage(DynamicDrawUsage);
    });
  }, []);

  const resolvePowerAlert = useCallback(
    (event: ThreeEvent<PointerEvent>): PowerAlertInstance | null => {
      if (event.instanceId == null || event.instanceId < 0) {
        return null;
      }

      return powerAlerts[event.instanceId] ?? null;
    },
    [powerAlerts]
  );

  const handlePointerOver = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      event.stopPropagation();
      const powerAlert = resolvePowerAlert(event);
      if (!powerAlert) {
        return;
      }

      const previous = hoveredPositionRef.current;
      if (
        previous &&
        previous.x === powerAlert.boardPosition.x &&
        previous.y === powerAlert.boardPosition.y
      ) {
        return;
      }

      hoveredPositionRef.current = powerAlert.boardPosition;
      onBoardPointerOver?.(event, powerAlert.boardPosition);
    },
    [onBoardPointerOver, resolvePowerAlert]
  );

  const handlePointerOut = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      event.stopPropagation();
      const hoveredPosition = hoveredPositionRef.current;
      if (!hoveredPosition) {
        return;
      }

      hoveredPositionRef.current = null;
      onBoardPointerOut?.(event, hoveredPosition);
    },
    [onBoardPointerOut]
  );

  const handlePointerDown = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      event.stopPropagation();
      const powerAlert = resolvePowerAlert(event);
      if (powerAlert) {
        onBoardPointerDown?.(event, powerAlert.boardPosition);
      }
    },
    [onBoardPointerDown, resolvePowerAlert]
  );

  const handlePointerUp = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      event.stopPropagation();
      const powerAlert = resolvePowerAlert(event);
      if (powerAlert) {
        onBoardPointerUp?.(event, powerAlert.boardPosition);
      }
    },
    [onBoardPointerUp, resolvePowerAlert]
  );

  useFrame(({ clock }) => {
    const boltMesh = boltRef.current;
    const haloMesh = haloRef.current;
    const glowMesh = glowRef.current;
    if (!boltMesh || !haloMesh || !glowMesh) {
      return;
    }

    const elapsed = clock.getElapsedTime();
    transform.quaternion.copy(camera.quaternion);

    powerAlerts.forEach((powerAlert, index) => {
      const cycleTime =
        ((elapsed + powerAlert.pulseOffset * POWER_ALERT_PULSE_PERIOD) %
          POWER_ALERT_PULSE_PERIOD) /
        POWER_ALERT_PULSE_PERIOD;
      const pulse = easeInOutSine(cycleTime);
      const scale = (0.85 + pulse * 0.25) * POWER_ALERT_SCALE_FACTOR;

      transform.position.set(
        powerAlert.position[0],
        powerAlert.position[1],
        powerAlert.position[2]
      );
      transform.scale.setScalar(scale);
      transform.updateMatrix();
      boltMesh.setMatrixAt(index, transform.matrix);
      haloMesh.setMatrixAt(index, transform.matrix);

      transform.position.set(
        powerAlert.position[0],
        powerAlert.position[1] - 0.05,
        powerAlert.position[2]
      );
      transform.scale.setScalar(scale * 1.08);
      transform.updateMatrix();
      glowMesh.setMatrixAt(index, transform.matrix);

      workingBoltColor.copy(boltColor).multiplyScalar(0.72 + pulse * 0.55);
      workingHaloColor.copy(haloColor).multiplyScalar(0.55 + pulse * 0.35);
      workingGlowColor.copy(glowColor).multiplyScalar(0.5 + pulse * 0.4);

      boltMesh.setColorAt(index, workingBoltColor);
      haloMesh.setColorAt(index, workingHaloColor);
      glowMesh.setColorAt(index, workingGlowColor);
    });

    [boltMesh, haloMesh, glowMesh].forEach(mesh => {
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) {
        mesh.instanceColor.needsUpdate = true;
      }
    });
  });

  if (powerAlerts.length === 0) {
    return null;
  }

  return (
    <group>
      <instancedMesh
        ref={boltRef}
        args={[resources.bolt, undefined, powerAlerts.length]}
        castShadow={false}
        receiveShadow={false}
        frustumCulled={false}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerOver}
        onPointerOut={handlePointerOut}
        onPointerOver={handlePointerOver}
        onPointerUp={handlePointerUp}
      >
        <meshStandardMaterial
          vertexColors
          emissive={POWER_ALERT_EMISSIVE}
          roughness={0.25}
          metalness={0.2}
          transparent
          opacity={0.78}
          emissiveIntensity={1.05}
          depthWrite={false}
        />
      </instancedMesh>
      <instancedMesh
        ref={haloRef}
        args={[resources.halo, undefined, powerAlerts.length]}
        castShadow={false}
        receiveShadow={false}
        frustumCulled={false}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerOver}
        onPointerOut={handlePointerOut}
        onPointerOver={handlePointerOver}
        onPointerUp={handlePointerUp}
      >
        <meshStandardMaterial
          vertexColors
          emissive={POWER_ALERT_COLOR}
          roughness={0.4}
          metalness={0.1}
          transparent
          opacity={0.34}
          depthWrite={false}
        />
      </instancedMesh>
      <instancedMesh
        ref={glowRef}
        args={[resources.glow, undefined, powerAlerts.length]}
        castShadow={false}
        receiveShadow={false}
        frustumCulled={false}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerOver}
        onPointerOut={handlePointerOut}
        onPointerOver={handlePointerOver}
        onPointerUp={handlePointerUp}
      >
        <meshBasicMaterial
          vertexColors
          transparent
          opacity={0.24}
          depthWrite={false}
          depthTest={false}
          toneMapped={false}
        />
      </instancedMesh>
    </group>
  );
};

interface PreviewModelInstanceProps {
  baseScene: Group;
  groundOffset: number;
  position: [number, number, number];
  baseHeight: number;
  rotationY?: number;
}

const PreviewModelInstance: React.FC<PreviewModelInstanceProps> = ({
  baseScene,
  groundOffset,
  position,
  baseHeight,
  rotationY = 0,
}) => {
  const clonedScene = useMemo(() => {
    const scene = clone(baseScene) as Group;

    scene.traverse(object => {
      const mesh = object as Mesh;
      if ('isMesh' in mesh && mesh.isMesh) {
        if (Array.isArray(mesh.material)) {
          mesh.material = mesh.material.map(material =>
            (material as Material).clone()
          );
        } else if (mesh.material) {
          mesh.material = (mesh.material as Material).clone();
        }
      }
    });

    return scene;
  }, [baseScene]);
  const materialRefs = useRef<Material[]>([]);

  useEffect(() => {
    const materials: Material[] = [];
    clonedScene.traverse(object => {
      const mesh = object as Mesh;
      if ('isMesh' in mesh && mesh.isMesh) {
        const meshMaterials = Array.isArray(mesh.material)
          ? mesh.material
          : [mesh.material];

        meshMaterials.forEach(material => {
          const typedMaterial = material as Material;
          typedMaterial.transparent = true;
          typedMaterial.depthWrite = false;
          typedMaterial.opacity = PREVIEW_MIN_OPACITY;
          typedMaterial.needsUpdate = true;
          materials.push(typedMaterial);
        });
      }
    });

    materialRefs.current = materials;

    return () => {
      materialRefs.current = [];
    };
  }, [clonedScene]);

  useFrame(({ clock }) => {
    const elapsed = clock.getElapsedTime();
    const cycle = (elapsed % PREVIEW_PULSE_DURATION) / PREVIEW_PULSE_DURATION;
    const wave = (Math.sin(cycle * Math.PI * 2) + 1) / 2;
    const opacity =
      PREVIEW_MIN_OPACITY + (PREVIEW_MAX_OPACITY - PREVIEW_MIN_OPACITY) * wave;

    materialRefs.current.forEach(material => {
      material.opacity = opacity;
    });
  });

  const finalPosition = useMemo<[number, number, number]>(() => {
    const [x, y, z] = position;
    return [x, y + groundOffset + baseHeight, z];
  }, [baseHeight, groundOffset, position]);

  return (
    <primitive
      object={clonedScene}
      position={finalPosition}
      rotation={[0, rotationY, 0]}
    />
  );
};

const ItemModelInstance: React.FC<ItemModelInstanceProps> = ({
  baseScene,
  groundOffset,
  position,
  rotationY,
}) => {
  const clonedScene = useMemo(() => clone(baseScene) as Group, [baseScene]);

  const finalPosition = useMemo<[number, number, number]>(() => {
    const [x, y, z] = position;
    return [x, y + groundOffset + ITEM_MODEL_BASE_HEIGHT, z];
  }, [groundOffset, position]);

  return (
    <primitive
      object={clonedScene}
      position={finalPosition}
      rotation={[0, rotationY, 0]}
    />
  );
};

interface GenericModelInstanceProps {
  baseScene: Group;
  groundOffset: number;
  position: [number, number, number];
  rotationY?: number;
  baseHeight: number;
}

const GenericModelInstance: React.FC<GenericModelInstanceProps> = ({
  baseScene,
  groundOffset,
  position,
  rotationY = 0,
  baseHeight,
}) => {
  const clonedScene = useMemo(() => clone(baseScene) as Group, [baseScene]);

  const finalPosition = useMemo<[number, number, number]>(() => {
    const [x, y, z] = position;
    return [x, y + groundOffset + baseHeight, z];
  }, [baseHeight, groundOffset, position]);

  return (
    <primitive
      object={clonedScene}
      position={finalPosition}
      rotation={[0, rotationY, 0]}
    />
  );
};

const useDisableRaycast = <T extends Object3D>(
  ref: React.RefObject<T | null>
) => {
  useEffect(() => {
    const target = ref.current;

    if (!target) {
      return;
    }

    const originalRaycast = target.raycast;
    const noopRaycast: typeof originalRaycast = () => undefined;
    target.raycast = noopRaycast;

    return () => {
      target.raycast = originalRaycast;
    };
  }, [ref]);
};

interface ButterflyData {
  id: string;
  basePosition: [number, number, number];
  color: string;
  scale: number;
  orbitRadius: number;
  orbitSpeed: number;
  flutterSpeed: number;
  bobSpeed: number;
  phase: number;
}

type ButterflyProps = ButterflyData;

const Butterfly: React.FC<ButterflyProps> = ({
  basePosition,
  color,
  scale,
  orbitRadius,
  orbitSpeed,
  flutterSpeed,
  bobSpeed,
  phase,
}) => {
  const groupRef = useRef<Group | null>(null);
  const leftWingRef = useRef<Mesh | null>(null);
  const rightWingRef = useRef<Mesh | null>(null);
  const bodyRef = useRef<Mesh | null>(null);

  useDisableRaycast(groupRef);
  useDisableRaycast(leftWingRef);
  useDisableRaycast(rightWingRef);
  useDisableRaycast(bodyRef);

  useEffect(() => {
    if (groupRef.current) {
      groupRef.current.scale.setScalar(scale);
    }
  }, [scale]);

  useFrame(({ clock }) => {
    const elapsed = clock.getElapsedTime();
    const time = elapsed + phase;
    const orbitPhase = time * orbitSpeed;

    const offsetX = Math.cos(orbitPhase) * orbitRadius;
    const offsetZ = Math.sin(orbitPhase) * orbitRadius;
    const offsetY = Math.sin(time * bobSpeed) * BUTTERFLY_BOB_AMPLITUDE;

    if (groupRef.current) {
      groupRef.current.position.set(
        basePosition[0] + offsetX,
        basePosition[1] + offsetY,
        basePosition[2] + offsetZ
      );

      const headingX = -Math.sin(orbitPhase) * orbitRadius * orbitSpeed;
      const headingZ = Math.cos(orbitPhase) * orbitRadius * orbitSpeed;
      groupRef.current.rotation.y = Math.atan2(headingX, headingZ);
    }

    const wingAngle = Math.sin(time * flutterSpeed) * 0.45 + 0.6;

    if (leftWingRef.current) {
      leftWingRef.current.rotation.y = wingAngle;
    }

    if (rightWingRef.current) {
      rightWingRef.current.rotation.y = -wingAngle;
    }
  });

  return (
    <group ref={groupRef} position={basePosition}>
      <mesh ref={bodyRef} rotation={[Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
        <cylinderGeometry args={[0.01, 0.015, 0.12, 6]} />
        <meshStandardMaterial
          color='#3f2c25'
          roughness={0.5}
          metalness={0.1}
          transparent
          opacity={0.95}
          depthWrite={false}
        />
      </mesh>
      <mesh ref={leftWingRef} position={[-0.05, 0.02, 0]}>
        <planeGeometry args={[0.32, 0.22]} />
        <meshStandardMaterial
          color={color}
          roughness={0.3}
          metalness={0.05}
          side={DoubleSide}
          transparent
          opacity={0.85}
          emissive={color}
          emissiveIntensity={0.15}
          depthWrite={false}
        />
      </mesh>
      <mesh ref={rightWingRef} position={[0.05, 0.02, 0]}>
        <planeGeometry args={[0.32, 0.22]} />
        <meshStandardMaterial
          color={color}
          roughness={0.3}
          metalness={0.05}
          side={DoubleSide}
          transparent
          opacity={0.85}
          emissive={color}
          emissiveIntensity={0.15}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
};

interface ButterflySwarmProps {
  count?: number;
  playerPos: Position;
}

const ButterflySwarm: React.FC<ButterflySwarmProps> = ({
  count = BUTTERFLY_COUNT,
  playerPos,
}) => {
  const chunkX = Math.floor(playerPos.x / BUTTERFLY_CHUNK_SIZE);
  const chunkY = Math.floor(playerPos.y / BUTTERFLY_CHUNK_SIZE);
  const chunkOriginX = chunkX * BUTTERFLY_CHUNK_SIZE;
  const chunkOriginY = chunkY * BUTTERFLY_CHUNK_SIZE;
  const chunkCenterX = chunkOriginX + BUTTERFLY_CHUNK_SIZE / 2;
  const chunkCenterY = chunkOriginY + BUTTERFLY_CHUNK_SIZE / 2;

  const butterflies = useMemo<ButterflyData[]>(() => {
    const seed = hashChunkCoordinates(chunkX, chunkY);
    const random = createSeededRandom(seed);

    return Array.from({ length: count }, (_, index) => {
      const hue = 180 + random() * 90;
      const saturation = 60 + random() * 20;
      const lightness = 65 + random() * 15;
      const offsetX = (random() - 0.5) * BUTTERFLY_CHUNK_SIZE * TILE_SPACING;
      const offsetZ = (random() - 0.5) * BUTTERFLY_CHUNK_SIZE * TILE_SPACING;

      return {
        id: `${chunkX}:${chunkY}:${index}`,
        basePosition: [offsetX, 0.8 + random() * 0.5, offsetZ],
        color: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
        scale: 0.35 + random() * 0.25,
        orbitRadius: 0.35 + random() * 0.85,
        orbitSpeed: 0.25 + random() * 0.35,
        flutterSpeed: 7 + random() * 4,
        bobSpeed: 0.8 + random() * 0.6,
        phase: random() * Math.PI * 2,
      };
    });
  }, [chunkX, chunkY, count]);

  const groupOffsetX = (chunkCenterX - playerPos.x) * TILE_SPACING;
  const groupOffsetZ = (chunkCenterY - playerPos.y) * TILE_SPACING;

  return (
    <group position={[groupOffsetX, 0, groupOffsetZ]}>
      {butterflies.map(butterfly => (
        <Butterfly key={butterfly.id} {...butterfly} />
      ))}
    </group>
  );
};

const SceneContent: React.FC<SceneContentProps> = ({
  tiles,
  terrainOffset,
  previewTiles,
  buildingPreview,
  assemblerPreview,
  targetPosition,
  normalizedZoom,
  playerFacing,
  playerPos,
  assemblers,
  belts,
  beltItems,
  buildingModels,
  resourceDeposits,
  powerAlerts,
  powerOverlayActive,
  loadedGltfs,
  onBoardPointerDown,
  onBoardPointerUp,
  onBoardPointerOver,
  onBoardPointerOut,
}) => {
  const { camera } = useThree();

  useEffect(() => {
    const baseDistance = 24 / normalizedZoom;
    camera.position.set(0, baseDistance, baseDistance);
    camera.up.set(0, 1, 0);
    camera.far = 5000;
    camera.updateProjectionMatrix();
    camera.lookAt(0, 0, 0);
  }, [camera, normalizedZoom]);

  const playerRotation = useMemo(
    () => getFacingRotation(playerFacing) + Math.PI,
    [playerFacing]
  );

  const assemblerGltf = getRequiredGltf(loadedGltfs, 'assembler');
  const basicDrillGltf = getRequiredGltf(loadedGltfs, 'basicDrill');
  const advancedDrillGltf = getRequiredGltf(loadedGltfs, 'advancedDrill');
  const sandMinerGltf = getRequiredGltf(loadedGltfs, 'sandMiner');
  const generatorGltf = getRequiredGltf(loadedGltfs, 'generator');
  const solarPanelGltf = getRequiredGltf(loadedGltfs, 'solarPanel');
  const researchStationGltf = getRequiredGltf(loadedGltfs, 'researchStation');
  const splitterGltf = getRequiredGltf(loadedGltfs, 'splitter');
  const undergroundBeltGltf = getRequiredGltf(loadedGltfs, 'undergroundBelt');
  const factoryGltf = getRequiredGltf(loadedGltfs, 'factory');
  const rocketSiloGltf = getRequiredGltf(loadedGltfs, 'rocketSilo');
  const ironChestGltf = getRequiredGltf(loadedGltfs, 'ironChest');
  const steelChestGltf = getRequiredGltf(loadedGltfs, 'steelChest');
  const ironDepositGltf = getRequiredGltf(loadedGltfs, 'ironDeposit');
  const copperDepositGltf = getRequiredGltf(loadedGltfs, 'copperDeposit');
  const ironOreGltf = getRequiredGltf(loadedGltfs, 'ironOre');
  const copperOreGltf = getRequiredGltf(loadedGltfs, 'copperOre');
  const gearGltf = getRequiredGltf(loadedGltfs, 'gear');
  const ironPlateGltf = getRequiredGltf(loadedGltfs, 'ironPlate');
  const copperPlateGltf = getRequiredGltf(loadedGltfs, 'copperPlate');
  const basicCircuitGltf = getRequiredGltf(loadedGltfs, 'basicCircuit');
  const advancedCircuitGltf = getRequiredGltf(loadedGltfs, 'advancedCircuit');
  const transistorGltf = getRequiredGltf(loadedGltfs, 'transistor');
  const sandGltf = getRequiredGltf(loadedGltfs, 'sand');
  const playerGltf = getRequiredGltf(loadedGltfs, 'player');

  const assemblerModelData = useMemo(() => {
    const baseScene = assemblerGltf.scene.clone(true);
    const boundingBox = new Box3().setFromObject(baseScene);
    const size = new Vector3();
    boundingBox.getSize(size);
    const maxHorizontalSpan = Math.max(size.x, size.z, 0.001);
    const desiredSpan = TILE_SPACING * ASSEMBLER_TILE_SIZE;
    const scale = desiredSpan / maxHorizontalSpan;

    baseScene.scale.setScalar(scale);
    baseScene.position.set(0, 0, 0);

    const scaledBox = new Box3().setFromObject(baseScene);
    const groundOffset = -scaledBox.min.y;

    baseScene.traverse(object => {
      if ('isMesh' in object && object.isMesh) {
        object.castShadow = true;
        object.receiveShadow = true;
      }
    });

    return {
      baseScene,
      groundOffset,
    };
  }, [assemblerGltf]);

  const powerAlertResources = useMemo(() => createPowerAlertResources(), []);

  useEffect(
    () => () => {
      powerAlertResources.bolt.dispose();
      powerAlertResources.halo.dispose();
      powerAlertResources.glow.dispose();
    },
    [powerAlertResources]
  );

  const basicDrillModelData = useMemo(
    () => prepareModelData(basicDrillGltf.scene, TILE_SPACING * 0.9),
    [basicDrillGltf]
  );
  const advancedDrillModelData = useMemo(
    () => prepareModelData(advancedDrillGltf.scene, TILE_SPACING * 0.9),
    [advancedDrillGltf]
  );
  const sandMinerModelData = useMemo(
    () => prepareModelData(sandMinerGltf.scene, TILE_SPACING * 0.9),
    [sandMinerGltf]
  );
  const generatorModelData = useMemo(
    () => prepareModelData(generatorGltf.scene, TILE_SPACING * 1.1),
    [generatorGltf]
  );
  const solarPanelModelData = useMemo(
    () => prepareModelData(solarPanelGltf.scene, TILE_SPACING * 1.1),
    [solarPanelGltf]
  );
  const researchStationModelData = useMemo(
    () =>
      prepareModelData(
        researchStationGltf.scene,
        TILE_SPACING * ASSEMBLER_TILE_SIZE
      ),
    [researchStationGltf]
  );
  const splitterModelData = useMemo(
    () => prepareModelData(splitterGltf.scene, TILE_SPACING),
    [splitterGltf]
  );
  const undergroundBeltModelData = useMemo(
    () => prepareModelData(undergroundBeltGltf.scene, TILE_SPACING),
    [undergroundBeltGltf]
  );
  const factoryModelData = useMemo(
    () =>
      prepareModelData(factoryGltf.scene, TILE_SPACING * ASSEMBLER_TILE_SIZE),
    [factoryGltf]
  );
  const rocketSiloModelData = useMemo(
    () =>
      prepareModelData(
        rocketSiloGltf.scene,
        TILE_SPACING * ROCKET_SILO_TILE_SIZE
      ),
    [rocketSiloGltf]
  );
  const ironChestModelData = useMemo(
    () => prepareModelData(ironChestGltf.scene, TILE_SPACING * 0.95),
    [ironChestGltf]
  );
  const steelChestModelData = useMemo(
    () => prepareModelData(steelChestGltf.scene, TILE_SPACING * 0.95),
    [steelChestGltf]
  );
  const ironDepositModelData = useMemo(
    () => prepareModelData(ironDepositGltf.scene, TILE_SPACING * 0.95),
    [ironDepositGltf]
  );
  const copperDepositModelData = useMemo(
    () => prepareModelData(copperDepositGltf.scene, TILE_SPACING * 0.95),
    [copperDepositGltf]
  );

  const buildingModelDataMap = useMemo(
    () =>
      new Map<BuildingModelType, ItemModelData>([
        ['basicDrill', basicDrillModelData],
        ['advancedDrill', advancedDrillModelData],
        ['sandMiner', sandMinerModelData],
        ['generator', generatorModelData],
        ['solarPanel', solarPanelModelData],
        ['researchStation', researchStationModelData],
        ['splitter', splitterModelData],
        ['undergroundBelt', undergroundBeltModelData],
        ['factory', factoryModelData],
        ['rocketSilo', rocketSiloModelData],
        ['ironChest', ironChestModelData],
        ['steelChest', steelChestModelData],
      ]),
    [
      basicDrillModelData,
      advancedDrillModelData,
      generatorModelData,
      rocketSiloModelData,
      researchStationModelData,
      sandMinerModelData,
      solarPanelModelData,
      splitterModelData,
      undergroundBeltModelData,
      factoryModelData,
      ironChestModelData,
      steelChestModelData,
    ]
  );

  const resourceDepositModelDataMap = useMemo(
    () =>
      new Map<ResourceDepositType, ItemModelData>([
        ['iron', ironDepositModelData],
        ['copper', copperDepositModelData],
      ]),
    [copperDepositModelData, ironDepositModelData]
  );

  const ironOreModelData = useMemo(
    () => prepareModelData(ironOreGltf.scene, TILE_SPACING * 0.4),
    [ironOreGltf]
  );

  const copperOreModelData = useMemo(
    () => prepareModelData(copperOreGltf.scene, TILE_SPACING * 0.4),
    [copperOreGltf]
  );

  const ironPlateModelData = useMemo(
    () => prepareModelData(ironPlateGltf.scene, TILE_SPACING * 0.42),
    [ironPlateGltf]
  );

  const copperPlateModelData = useMemo(
    () => prepareModelData(copperPlateGltf.scene, TILE_SPACING * 0.42),
    [copperPlateGltf]
  );
  const gearModelData = useMemo(
    () => prepareModelData(gearGltf.scene, TILE_SPACING * 0.4),
    [gearGltf]
  );
  const basicCircuitModelData = useMemo(
    () => prepareModelData(basicCircuitGltf.scene, TILE_SPACING * 0.36),
    [basicCircuitGltf]
  );
  const advancedCircuitModelData = useMemo(
    () => prepareModelData(advancedCircuitGltf.scene, TILE_SPACING * 0.38),
    [advancedCircuitGltf]
  );
  const transistorModelData = useMemo(
    () => prepareModelData(transistorGltf.scene, TILE_SPACING * 0.18),
    [transistorGltf]
  );
  const steelChestItemModelData = useMemo(
    () => prepareModelData(steelChestGltf.scene, TILE_SPACING * 0.55),
    [steelChestGltf]
  );
  const sandModelData = useMemo(
    () => prepareModelData(sandGltf.scene, TILE_SPACING * 0.36),
    [sandGltf]
  );

  const playerModelData = useMemo(
    () => prepareModelData(playerGltf.scene, TILE_SPACING * 0.55),
    [playerGltf]
  );

  const beltItemModelMap = useMemo(
    () =>
      new Map<ItemType, ItemModelData>([
        ['iron-ore', ironOreModelData],
        ['copper-ore', copperOreModelData],
        ['gear', gearModelData],
        ['iron-plate', ironPlateModelData],
        ['copper-plate', copperPlateModelData],
        ['circuit', basicCircuitModelData],
        ['advanced-circuit', advancedCircuitModelData],
        ['sand', sandModelData],
        ['transistor', transistorModelData],
        ['steel-chest', steelChestItemModelData],
      ]),
    [
      copperOreModelData,
      copperPlateModelData,
      ironOreModelData,
      ironPlateModelData,
      gearModelData,
      basicCircuitModelData,
      advancedCircuitModelData,
      sandModelData,
      transistorModelData,
      steelChestItemModelData,
    ]
  );

  return (
    <>
      <color attach='background' args={['#0d1218']} />
      <ambientLight intensity={0.35} />
      <hemisphereLight args={['#dfe9ff', '#1a2421', 0.85]} />
      <directionalLight
        position={[32, 42, 22]}
        intensity={1}
        color='#fff2d6'
        castShadow
        shadow-mapSize={[2048, 2048]}
        shadow-bias={-0.0005}
        shadow-camera-near={2}
        shadow-camera-far={120}
        shadow-camera-left={-60}
        shadow-camera-right={60}
        shadow-camera-top={60}
        shadow-camera-bottom={-60}
      />
      <directionalLight
        position={[-28, 26, -35]}
        intensity={0.45}
        color='#c7d9ff'
      />

      <GroundBackdrop />

      <ButterflySwarm playerPos={playerPos} />

      <group position={terrainOffset}>
        <TerrainSurface
          tiles={tiles}
          onBoardPointerDown={onBoardPointerDown}
          onBoardPointerUp={onBoardPointerUp}
          onBoardPointerOver={onBoardPointerOver}
          onBoardPointerOut={onBoardPointerOut}
        />

        {resourceDeposits.length > 0 && (
          <group>
            {resourceDeposits.map(instance => {
              const modelData = resourceDepositModelDataMap.get(instance.type);
              if (!modelData || !modelData.baseScene) {
                return null;
              }

              return (
                <group
                  key={instance.key}
                  onPointerDown={(event: ThreeEvent<PointerEvent>) => {
                    event.stopPropagation();
                    onBoardPointerDown?.(event, instance.boardPosition);
                  }}
                  onPointerUp={(event: ThreeEvent<PointerEvent>) => {
                    event.stopPropagation();
                    onBoardPointerUp?.(event, instance.boardPosition);
                  }}
                  onPointerOver={(event: ThreeEvent<PointerEvent>) => {
                    event.stopPropagation();
                    onBoardPointerOver?.(event, instance.boardPosition);
                  }}
                  onPointerOut={(event: ThreeEvent<PointerEvent>) => {
                    event.stopPropagation();
                    onBoardPointerOut?.(event, instance.boardPosition);
                  }}
                >
                  <GenericModelInstance
                    baseScene={modelData.baseScene}
                    groundOffset={modelData.groundOffset}
                    position={instance.position}
                    baseHeight={RESOURCE_NODE_BASE_HEIGHT}
                  />
                </group>
              );
            })}
          </group>
        )}
      </group>

      {previewTiles.length > 0 && (
        <group>
          {previewTiles.map(preview => (
            <mesh
              key={preview.key}
              position={preview.position}
              rotation={[-Math.PI / 2, 0, 0]}
              onPointerDown={(event: ThreeEvent<PointerEvent>) => {
                event.stopPropagation();
                onBoardPointerDown?.(event, preview.boardPosition);
              }}
              onPointerUp={(event: ThreeEvent<PointerEvent>) => {
                event.stopPropagation();
                onBoardPointerUp?.(event, preview.boardPosition);
              }}
              onPointerOver={(event: ThreeEvent<PointerEvent>) => {
                event.stopPropagation();
                onBoardPointerOver?.(event, preview.boardPosition);
              }}
              onPointerOut={(event: ThreeEvent<PointerEvent>) => {
                event.stopPropagation();
                onBoardPointerOut?.(event, preview.boardPosition);
              }}
            >
              <planeGeometry args={[1, 1]} />
              <meshBasicMaterial
                color={preview.isInvalid ? 0xff6b6b : 0xffd166}
                transparent
                opacity={0.45}
                side={DoubleSide}
              />
            </mesh>
          ))}
        </group>
      )}

      {assemblerPreview && assemblerModelData.baseScene && (
        <PreviewModelInstance
          key={assemblerPreview.key}
          baseScene={assemblerModelData.baseScene}
          groundOffset={assemblerModelData.groundOffset}
          position={assemblerPreview.position}
          baseHeight={ASSEMBLER_BASE_HEIGHT}
        />
      )}

      {buildingPreview &&
        (() => {
          const modelData = buildingModelDataMap.get(buildingPreview.type);
          if (!modelData || !modelData.baseScene) {
            return null;
          }

          return (
            <PreviewModelInstance
              key={buildingPreview.key}
              baseScene={modelData.baseScene}
              groundOffset={modelData.groundOffset}
              position={buildingPreview.position}
              rotationY={buildingPreview.rotationY}
              baseHeight={GENERIC_BUILDING_BASE_HEIGHT}
            />
          );
        })()}

      {targetPosition && (
        <mesh position={targetPosition} rotation={[-Math.PI / 2, 0, 0]}>
          <planeGeometry args={[1.1, 1.1]} />
          <meshBasicMaterial
            color={0x7ef9ff}
            transparent
            opacity={0.35}
            side={DoubleSide}
          />
        </mesh>
      )}

      <InstancedBeltConveyors
        belts={belts}
        onBoardPointerDown={onBoardPointerDown}
        onBoardPointerUp={onBoardPointerUp}
        onBoardPointerOver={onBoardPointerOver}
      />

      {buildingModels.length > 0 && (
        <group>
          {buildingModels.map(instance => {
            const modelData = buildingModelDataMap.get(instance.type);
            if (!modelData || !modelData.baseScene) {
              return null;
            }

            return (
              <group
                key={instance.key}
                onPointerDown={(event: ThreeEvent<PointerEvent>) => {
                  event.stopPropagation();
                  onBoardPointerDown?.(event, instance.boardPosition);
                }}
                onPointerUp={(event: ThreeEvent<PointerEvent>) => {
                  event.stopPropagation();
                  onBoardPointerUp?.(event, instance.boardPosition);
                }}
                onPointerOver={(event: ThreeEvent<PointerEvent>) => {
                  event.stopPropagation();
                  onBoardPointerOver?.(event, instance.boardPosition);
                }}
                onPointerOut={(event: ThreeEvent<PointerEvent>) => {
                  event.stopPropagation();
                  onBoardPointerOut?.(event, instance.boardPosition);
                }}
              >
                <GenericModelInstance
                  baseScene={modelData.baseScene}
                  groundOffset={modelData.groundOffset}
                  position={instance.position}
                  rotationY={instance.rotationY}
                  baseHeight={GENERIC_BUILDING_BASE_HEIGHT}
                />
              </group>
            );
          })}
        </group>
      )}

      {powerOverlayActive && powerAlerts.length > 0 && (
        <InstancedPowerAlerts
          powerAlerts={powerAlerts}
          resources={powerAlertResources}
          onBoardPointerDown={onBoardPointerDown}
          onBoardPointerUp={onBoardPointerUp}
          onBoardPointerOver={onBoardPointerOver}
          onBoardPointerOut={onBoardPointerOut}
        />
      )}

      {beltItems.length > 0 && (
        <group>
          {beltItems.map(instance => {
            const modelData = beltItemModelMap.get(instance.itemType);
            if (!modelData) {
              return null;
            }

            return (
              <group
                key={instance.key}
                onPointerOver={(event: ThreeEvent<PointerEvent>) => {
                  event.stopPropagation();
                  onBoardPointerOver?.(event, instance.boardPosition);
                }}
                onPointerOut={(event: ThreeEvent<PointerEvent>) => {
                  event.stopPropagation();
                  onBoardPointerOut?.(event, instance.boardPosition);
                }}
              >
                <ItemModelInstance
                  baseScene={modelData.baseScene}
                  groundOffset={modelData.groundOffset}
                  position={instance.position}
                  rotationY={instance.rotationY}
                />
              </group>
            );
          })}
        </group>
      )}

      {assemblers.length > 0 && assemblerModelData.baseScene && (
        <group>
          {assemblers.map(instance => (
            <group
              key={instance.key}
              onPointerDown={(event: ThreeEvent<PointerEvent>) => {
                event.stopPropagation();
                onBoardPointerDown?.(event, instance.boardPosition);
              }}
              onPointerUp={(event: ThreeEvent<PointerEvent>) => {
                event.stopPropagation();
                onBoardPointerUp?.(event, instance.boardPosition);
              }}
              onPointerOver={(event: ThreeEvent<PointerEvent>) => {
                event.stopPropagation();
                onBoardPointerOver?.(event, instance.boardPosition);
              }}
              onPointerOut={(event: ThreeEvent<PointerEvent>) => {
                event.stopPropagation();
                onBoardPointerOut?.(event, instance.boardPosition);
              }}
            >
              <AssemblerModelInstance
                baseScene={assemblerModelData.baseScene}
                groundOffset={assemblerModelData.groundOffset}
                position={instance.position}
              />
            </group>
          ))}
        </group>
      )}

      {playerModelData.baseScene && (
        <GenericModelInstance
          baseScene={playerModelData.baseScene}
          groundOffset={playerModelData.groundOffset}
          position={[0, 0, 0]}
          rotationY={playerRotation}
          baseHeight={PLAYER_MODEL_BASE_HEIGHT}
        />
      )}
    </>
  );
};

const StatsBridge: React.FC<{ stats: Stats | null }> = ({ stats }) => {
  useFrame(() => {
    stats?.update();
  });

  return null;
};

export const ThreeRenderer: React.FC = () => {
  const [isCanvasReady, setIsCanvasReady] = useState(false);
  const [stats, setStats] = useState<Stats | null>(null);
  const [modelLoadingState, setModelLoadingState] = useState<ModelLoadingState>(
    INITIAL_MODEL_LOADING_STATE
  );
  const [loadedGltfs, setLoadedGltfs] = useState<LoadedGltfMap | null>(null);
  const [modelLoadError, setModelLoadError] = useState<string | null>(null);
  const [hasEnteredScene, setHasEnteredScene] = useState(false);
  const [modelReloadToken, setModelReloadToken] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [powerOverlayActive, setPowerOverlayActive] = useState(false);

  const loadingManager = useMemo(() => {
    const manager = new LoadingManager();
    const originalItemStart = manager.itemStart.bind(manager);
    const originalItemError = manager.itemError.bind(manager);

    manager.itemStart = (url: string) => {
      setModelLoadingState(prev => ({
        ...prev,
        activeLabel: getModelLabelFromUrl(url),
      }));
      originalItemStart(url);
    };

    manager.itemError = (url: string) => {
      setModelLoadingState(prev => ({
        ...prev,
        isLoading: false,
        activeLabel: getModelLabelFromUrl(url),
      }));
      originalItemError(url);
    };

    manager.onStart = (
      url: string,
      itemsLoaded: number,
      itemsTotal: number
    ) => {
      const progressPercentage = calculateModelLoadingProgress(
        itemsLoaded,
        itemsTotal,
        false
      );

      setModelLoadingState({
        isLoading: true,
        itemsLoaded,
        itemsTotal,
        activeLabel: getModelLabelFromUrl(url),
        progressPercentage,
      });
    };

    manager.onProgress = (
      _url: string,
      itemsLoaded: number,
      itemsTotal: number
    ) => {
      const progressPercentage = calculateModelLoadingProgress(
        itemsLoaded,
        itemsTotal,
        false
      );

      setModelLoadingState(prev => ({
        isLoading: itemsLoaded < itemsTotal,
        itemsLoaded,
        itemsTotal,
        activeLabel: itemsLoaded < itemsTotal ? prev.activeLabel : null,
        progressPercentage,
      }));
    };

    manager.onLoad = () => {
      setModelLoadingState(prev => ({
        isLoading: false,
        itemsLoaded: prev.itemsTotal || prev.itemsLoaded,
        itemsTotal: prev.itemsTotal,
        activeLabel: null,
        progressPercentage: calculateModelLoadingProgress(
          prev.itemsTotal || prev.itemsLoaded,
          prev.itemsTotal,
          true
        ),
      }));
    };

    return manager;
  }, [setModelLoadingState]);
  const gameEngine = useMemo(() => getGameEngine(), []);
  const gltfModelUrls = useMemo(
    () =>
      Array.from(GLTF_MODEL_RELATIVE_PATHS.values(), relativePath =>
        resolveAssetUrl(relativePath)
      ),
    []
  );

  useEffect(() => {
    let isMounted = true;
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath(resolveAssetUrl('/assets/draco/'));
    const loader = new GLTFLoader(loadingManager);
    loader.setDRACOLoader(dracoLoader);

    setModelLoadingState(INITIAL_MODEL_LOADING_STATE);
    setModelLoadError(null);
    setLoadedGltfs(null);

    Promise.all(gltfModelUrls.map(url => loader.loadAsync(url)))
      .then(results => {
        if (!isMounted) {
          return;
        }

        setLoadedGltfs(
          createLoadedGltfMap(
            results as LoadedGltf[],
            GLTF_MODEL_RELATIVE_PATH_ENTRIES
          )
        );
      })
      .catch(error => {
        if (!isMounted) {
          return;
        }

        // eslint-disable-next-line no-console
        console.error('Failed to load 3D renderer assets', error);
        setModelLoadError('Failed to load the 3D renderer. Please try again.');
      })
      .finally(() => {
        dracoLoader.dispose();
      });

    return () => {
      isMounted = false;
      dracoLoader.dispose();
    };
  }, [gltfModelUrls, loadingManager, modelReloadToken]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const statsInstance = new Stats();
    statsInstance.showPanel(0);
    statsInstance.dom.classList.add('three-renderer__stats');
    statsInstance.dom.style.display = 'none';
    statsInstance.dom.style.position = 'absolute';
    statsInstance.dom.style.top = '12px';
    statsInstance.dom.style.left = '12px';
    statsInstance.dom.style.right = 'auto';
    statsInstance.dom.style.bottom = 'auto';
    statsInstance.dom.style.zIndex = '10';
    statsInstance.dom.style.pointerEvents = 'none';
    statsInstance.dom.style.width = '60px';
    statsInstance.dom.style.height = '60px';
    statsInstance.dom.style.overflow = 'hidden';
    container.appendChild(statsInstance.dom);
    setStats(statsInstance);

    return () => {
      if (statsInstance.dom.parentElement) {
        statsInstance.dom.parentElement.removeChild(statsInstance.dom);
      }
    };
  }, []);

  useEffect(() => {
    if (!stats) {
      return;
    }

    stats.dom.style.display = isCanvasReady ? 'block' : 'none';
  }, [isCanvasReady, stats]);

  useEffect(() => {
    if (!hasEnteredScene) {
      setIsCanvasReady(false);
    }
  }, [hasEnteredScene]);

  const mapState = useMapState();
  const uiState = useUIState();

  const {
    gameMap,
    buildingLayer,
    beltContentLayer,
    playerPos,
    playerFacing,
    powerSatisfaction,
    getTile,
    getBiome,
    getTargetPosition,
    getBuildingTargetLocation,
    zoomLevel,
    resourceNodeMenu,
    closeResourceNodeMenu,
    quickBeltPreview,
    startQuickBeltPlacement,
    extendQuickBeltPlacement,
    finalizeQuickBeltPreview,
    cancelQuickBeltPlacement,
  } = mapState;

  useEffect(() => {
    let intervalId: number | undefined;
    let timeoutId: number | undefined;

    const triggerOverlay = () => {
      setPowerOverlayActive(true);
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
      timeoutId = window.setTimeout(() => {
        setPowerOverlayActive(false);
        timeoutId = undefined;
      }, POWER_ALERT_PULSE_PERIOD * 1000);
    };

    if (powerSatisfaction < 1) {
      triggerOverlay();
      intervalId = window.setInterval(() => {
        triggerOverlay();
      }, 2500);
    } else {
      setPowerOverlayActive(false);
    }

    return () => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [powerSatisfaction]);
  const {
    buildMode,
    selectedBuilding,
    buildMenuOpen,
    researchMenuOpen,
    inventoryOpen,
    helpMenuOpen,
    saveMenuOpen,
    debugMenuOpen,
    leftPanelOpen,
    achievementMenuOpen,
    buildingStatusMenuTarget,
    setBuildMenuState,
    setResearchMenuOpen,
    setInventoryOpen,
    setHelpMenuOpen,
    setSaveMenuOpen,
    setDebugMenuOpen,
    setLeftPanelOpen,
    setAchievementMenuOpen,
    exitBuildMode,
    closeBuildingStatusMenu,
  } = uiState;

  const handleBoardPositionClick = useCallback(
    (position: Position) => {
      let consumed = false;

      if (buildMenuOpen) {
        setBuildMenuState({
          buildMenuOpen: false,
          selectedCategory: null,
          selectedBuilding: null,
        });
        exitBuildMode();
        consumed = true;
      }

      if (researchMenuOpen) {
        setResearchMenuOpen(false);
        consumed = true;
      }

      if (inventoryOpen) {
        setInventoryOpen(false);
        consumed = true;
      }

      if (helpMenuOpen) {
        setHelpMenuOpen(false);
        consumed = true;
      }

      if (saveMenuOpen) {
        setSaveMenuOpen(false);
        consumed = true;
      }

      if (debugMenuOpen) {
        setDebugMenuOpen(false);
        consumed = true;
      }

      if (leftPanelOpen) {
        setLeftPanelOpen(false);
        consumed = true;
      }

      if (achievementMenuOpen) {
        setAchievementMenuOpen(false);
        consumed = true;
      }

      if (buildingStatusMenuTarget) {
        closeBuildingStatusMenu();
        consumed = true;
      }

      if (resourceNodeMenu) {
        closeResourceNodeMenu();
        consumed = true;
      }

      if (consumed) {
        return;
      }

      if (buildMode) {
        if (selectedBuilding) {
          InputManager.getInstance().triggerTakeActionAt(position);
        }
        return;
      }

      const distance =
        Math.abs(position.x - playerPos.x) + Math.abs(position.y - playerPos.y);

      if (distance > 50) {
        return;
      }

      InputManager.getInstance().triggerTakeActionAt(position);
    },
    [
      achievementMenuOpen,
      buildMenuOpen,
      buildMode,
      buildingStatusMenuTarget,
      selectedBuilding,
      closeBuildingStatusMenu,
      closeResourceNodeMenu,
      debugMenuOpen,
      exitBuildMode,
      helpMenuOpen,
      inventoryOpen,
      leftPanelOpen,
      playerPos.x,
      playerPos.y,
      researchMenuOpen,
      resourceNodeMenu,
      saveMenuOpen,
      setAchievementMenuOpen,
      setBuildMenuState,
      setDebugMenuOpen,
      setHelpMenuOpen,
      setInventoryOpen,
      setLeftPanelOpen,
      setResearchMenuOpen,
      setSaveMenuOpen,
    ]
  );

  const handleBoardPositionHover = useCallback(
    (position: Position | null) => {
      gameEngine.setHoverTargetPosition(position);
    },
    [gameEngine]
  );

  const quickBeltDragRef = useRef<QuickBeltDragState | null>(null);
  const quickBeltLastPositionRef = useRef<string | null>(null);
  const quickBeltDragCleanupRef = useRef<(() => void) | null>(null);
  const pointerDownStateRef = useRef<{
    pointerId: number;
    position: Position;
  } | null>(null);

  const cleanupQuickBeltDrag = useCallback(() => {
    quickBeltDragCleanupRef.current?.();
    quickBeltDragCleanupRef.current = null;
    quickBeltDragRef.current = null;
    quickBeltLastPositionRef.current = null;
  }, []);

  const handleQuickBeltPointerUp = useCallback(
    (event: PointerEvent) => {
      const drag = quickBeltDragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) {
        return;
      }

      finalizeQuickBeltPreview();
      if (
        pointerDownStateRef.current &&
        pointerDownStateRef.current.pointerId === event.pointerId
      ) {
        pointerDownStateRef.current = null;
      }
      cleanupQuickBeltDrag();
    },
    [cleanupQuickBeltDrag, finalizeQuickBeltPreview]
  );

  const handleQuickBeltPointerCancel = useCallback(
    (event: PointerEvent) => {
      const drag = quickBeltDragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) {
        return;
      }

      cancelQuickBeltPlacement();
      if (
        pointerDownStateRef.current &&
        pointerDownStateRef.current.pointerId === event.pointerId
      ) {
        pointerDownStateRef.current = null;
      }
      cleanupQuickBeltDrag();
    },
    [cancelQuickBeltPlacement, cleanupQuickBeltDrag]
  );

  useEffect(() => cleanupQuickBeltDrag, [cleanupQuickBeltDrag]);

  const handleBoardPointerDown = useCallback(
    (event: ThreeEvent<PointerEvent>, position: Position) => {
      const isQuickBeltSelected =
        buildMode &&
        selectedBuilding &&
        (selectedBuilding.id === BuildingType.BELT ||
          selectedBuilding.id === BuildingType.FAST_BELT);

      pointerDownStateRef.current = {
        pointerId: event.pointerId,
        position,
      };

      if (
        !isQuickBeltSelected ||
        (event.pointerType === 'mouse' && event.button !== 0) ||
        !event.isPrimary
      ) {
        return;
      }

      const beltType = selectedBuilding.id as BeltType;
      pointerDownStateRef.current = {
        pointerId: event.pointerId,
        position,
      };

      cleanupQuickBeltDrag();
      cancelQuickBeltPlacement();
      startQuickBeltPlacement(beltType, position);
      quickBeltDragRef.current = {
        pointerId: event.pointerId,
        beltType,
      };
      quickBeltLastPositionRef.current = `${position.x},${position.y}`;

      window.addEventListener('pointerup', handleQuickBeltPointerUp, {
        passive: false,
      });
      window.addEventListener('pointercancel', handleQuickBeltPointerCancel, {
        passive: false,
      });
      quickBeltDragCleanupRef.current = () => {
        window.removeEventListener('pointerup', handleQuickBeltPointerUp);
        window.removeEventListener(
          'pointercancel',
          handleQuickBeltPointerCancel
        );
      };
    },
    [
      buildMode,
      cancelQuickBeltPlacement,
      cleanupQuickBeltDrag,
      handleQuickBeltPointerCancel,
      handleQuickBeltPointerUp,
      selectedBuilding,
      startQuickBeltPlacement,
    ]
  );

  const handleBoardPointerUp = useCallback(
    (event: ThreeEvent<PointerEvent>, position: Position) => {
      const drag = quickBeltDragRef.current;
      if (drag && drag.pointerId === event.pointerId) {
        return;
      }

      const state = pointerDownStateRef.current;
      pointerDownStateRef.current = null;

      if (
        !state ||
        state.pointerId !== event.pointerId ||
        state.position.x !== position.x ||
        state.position.y !== position.y
      ) {
        return;
      }

      handleBoardPositionClick(position);
    },
    [handleBoardPositionClick]
  );

  const handleBoardPointerOver = useCallback(
    (event: ThreeEvent<PointerEvent>, position: Position) => {
      handleBoardPositionHover(position);

      const drag = quickBeltDragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) {
        return;
      }

      const key = `${position.x},${position.y}`;
      if (quickBeltLastPositionRef.current === key) {
        return;
      }

      quickBeltLastPositionRef.current = key;
      extendQuickBeltPlacement(position);
    },
    [extendQuickBeltPlacement, handleBoardPositionHover]
  );

  const handleBoardPointerOut = useCallback(
    (_event: ThreeEvent<PointerEvent>, _position: Position) => {},
    []
  );

  const handleCanvasPointerLeave = useCallback(() => {
    gameEngine.setHoverTargetPosition(null);
  }, [gameEngine]);

  const handleEnterScene = useCallback(() => {
    if (!loadedGltfs || modelLoadingState.isLoading) {
      return;
    }

    setHasEnteredScene(true);
    gameEngine.setAudioSuspended(false);
    gameEngine.startMusic();
  }, [gameEngine, loadedGltfs, modelLoadingState.isLoading]);

  const handleRetryModelLoad = useCallback(() => {
    if (modelLoadingState.isLoading) {
      return;
    }

    setHasEnteredScene(false);
    setIsCanvasReady(false);
    setModelReloadToken(token => token + 1);
  }, [modelLoadingState.isLoading]);

  let computedTargetPosition: Position | null = null;
  try {
    computedTargetPosition = getTargetPosition(selectedBuilding ?? undefined);
  } catch {
    computedTargetPosition = null;
  }

  const targetPositionX = computedTargetPosition?.x ?? null;
  const targetPositionY = computedTargetPosition?.y ?? null;

  const targetPositionSnapshot = useMemo<Position | null>(() => {
    if (targetPositionX === null || targetPositionY === null) {
      return null;
    }

    return { x: targetPositionX, y: targetPositionY };
  }, [targetPositionX, targetPositionY]);

  let computedBuildingTargetPosition: Position | null = null;
  if (buildMode && selectedBuilding) {
    try {
      computedBuildingTargetPosition =
        getBuildingTargetLocation(selectedBuilding);
    } catch {
      computedBuildingTargetPosition = computedTargetPosition;
    }
  }

  const buildingTargetX = computedBuildingTargetPosition?.x ?? null;
  const buildingTargetY = computedBuildingTargetPosition?.y ?? null;

  const buildingTargetSnapshot = useMemo<Position | null>(() => {
    if (buildingTargetX === null || buildingTargetY === null) {
      return null;
    }

    return { x: buildingTargetX, y: buildingTargetY };
  }, [buildingTargetX, buildingTargetY]);

  const terrainSceneData = useMemo<TerrainSceneData>(() => {
    const normalizedZoom = Math.max(0.75, Math.min(2.5, zoomLevel));
    const visibleRadius = Math.max(
      6,
      Math.min(22, Math.round(TILE_RADIUS / normalizedZoom))
    );
    const terrainRadius = visibleRadius + TERRAIN_CACHE_MARGIN;
    const terrainOriginX =
      Math.floor(playerPos.x / TERRAIN_CACHE_STEP) * TERRAIN_CACHE_STEP;
    const terrainOriginY =
      Math.floor(playerPos.y / TERRAIN_CACHE_STEP) * TERRAIN_CACHE_STEP;

    const tiles: TileInstance[] = [];
    const resourceDeposits: ResourceDepositInstance[] = [];

    for (
      let y = terrainOriginY - terrainRadius;
      y <= terrainOriginY + terrainRadius;
      y += 1
    ) {
      for (
        let x = terrainOriginX - terrainRadius;
        x <= terrainOriginX + terrainRadius;
        x += 1
      ) {
        const terrainChar = getTile(x, y, gameMap);
        const biome = getBiome(x, y);
        const key = `${x},${y}`;
        const buildingChar = buildingLayer[key];
        const building = buildingChar
          ? findBuildingByCharacter(buildingChar)
          : null;
        const relativeX = (x - terrainOriginX) * TILE_SPACING;
        const relativeZ = (y - terrainOriginY) * TILE_SPACING;

        let depositType: ResourceDepositType | null = null;
        if (terrainChar === GAME_CONFIG.IRON_NODE_CHARACTER) {
          depositType = 'iron';
        } else if (terrainChar === GAME_CONFIG.COPPER_NODE_CHARACTER) {
          depositType = 'copper';
        }

        const buildingRequiresResourceNode = Boolean(
          building &&
            building.category === 'mining' &&
            building.requiresResourceNode !== false
        );

        if (depositType && (!building || buildingRequiresResourceNode)) {
          resourceDeposits.push({
            key: `deposit-${depositType}-${key}`,
            position: [relativeX, 0, relativeZ],
            type: depositType,
            boardPosition: { x, y },
          });
        }

        const tileColor = getTileColor(terrainChar, building);

        if (tileColor === null) {
          continue;
        }

        const hasBuilding = Boolean(building);
        const isEmptyTerrain = terrainChar === GAME_CONFIG.EMPTY_CHARACTER;
        const height = hasBuilding ? BUILDING_HEIGHT : TERRAIN_HEIGHT;

        tiles.push({
          key: `tile-${key}`,
          position: [relativeX, height / 2 - 0.1, relativeZ] as [
            number,
            number,
            number,
          ],
          height,
          color: tileColor,
          boardPosition: { x, y },
          biomeName: biome.name,
          isEmpty: isEmptyTerrain,
          terrainChar,
          hasBuilding,
        });
      }
    }

    return {
      normalizedZoom,
      origin: { x: terrainOriginX, y: terrainOriginY },
      tiles,
      resourceDeposits,
    };
  }, [
    buildingLayer,
    gameMap,
    getBiome,
    getTile,
    playerPos.x,
    playerPos.y,
    zoomLevel,
  ]);

  const sceneData = useMemo(() => {
    const { normalizedZoom, origin, resourceDeposits, tiles } =
      terrainSceneData;
    const assemblers: AssemblerInstance[] = [];
    const belts: BeltInstance[] = [];
    const beltItems: BeltItemInstance[] = [];
    const buildingModels: BuildingModelInstance[] = [];
    const powerAlerts: PowerAlertInstance[] = [];
    const terrainOffset: [number, number, number] = [
      (origin.x - playerPos.x) * TILE_SPACING,
      0,
      (origin.y - playerPos.y) * TILE_SPACING,
    ];

    Object.entries(buildingLayer).forEach(([posKey, buildingChar]) => {
      const building = findBuildingByCharacter(buildingChar);
      if (!building) {
        return;
      }

      const [centerXString, centerYString] = posKey.split(',');
      const centerX = Number(centerXString);
      const centerY = Number(centerYString);

      if (Number.isNaN(centerX) || Number.isNaN(centerY)) {
        return;
      }

      const definedWidth = building.size?.width ?? 1;
      const definedHeight = building.size?.height ?? 1;
      const isMultiTile = definedWidth > 1 || definedHeight > 1;

      let footprintWidth = definedWidth;
      let footprintHeight = definedHeight;

      let relativeX: number;
      let relativeZ: number;

      if (!isMultiTile) {
        relativeX = (centerX - playerPos.x) * TILE_SPACING;
        relativeZ = (centerY - playerPos.y) * TILE_SPACING;
      } else {
        let minX = centerX;
        let maxX = centerX;
        let minY = centerY;
        let maxY = centerY;

        while (buildingLayer[`${minX - 1},${centerY}`] === buildingChar) {
          minX -= 1;
        }
        while (buildingLayer[`${maxX + 1},${centerY}`] === buildingChar) {
          maxX += 1;
        }
        while (buildingLayer[`${centerX},${minY - 1}`] === buildingChar) {
          minY -= 1;
        }
        while (buildingLayer[`${centerX},${maxY + 1}`] === buildingChar) {
          maxY += 1;
        }

        footprintWidth = maxX - minX + 1;
        footprintHeight = maxY - minY + 1;
        const centerCandidateX = minX + Math.floor(footprintWidth / 2);
        const centerCandidateY = minY + Math.floor(footprintHeight / 2);

        if (centerX !== centerCandidateX || centerY !== centerCandidateY) {
          return;
        }

        relativeX = (centerCandidateX - playerPos.x) * TILE_SPACING;
        relativeZ = (centerCandidateY - playerPos.y) * TILE_SPACING;
      }

      const powerConsumption = building.powerConsumption ?? 0;
      if (powerConsumption > 0) {
        const alertHeight = getPowerAlertHeight(
          building,
          footprintWidth,
          footprintHeight
        );
        const pulseHash = hashChunkCoordinates(centerX, centerY);
        const pulseOffset = (pulseHash % 997) / 997;

        powerAlerts.push({
          key: `power-alert-${posKey}`,
          position: [relativeX, alertHeight, relativeZ],
          boardPosition: { x: centerX, y: centerY },
          pulseOffset,
        });
      }

      if (building.id === BuildingType.ASSEMBLER) {
        assemblers.push({
          key: `assembler-${posKey}`,
          position: [relativeX, 0, relativeZ],
          boardPosition: { x: centerX, y: centerY },
        });
        return;
      }

      if (
        building.id === BuildingType.BELT ||
        building.id === BuildingType.FAST_BELT
      ) {
        const facing = getBeltFacingFromCharacter(buildingChar);
        if (!facing) {
          return;
        }

        const rotationY = getBeltRotation(facing);
        const [directionX, directionZ] = getFacingDelta(facing);

        belts.push({
          key: `belt-${posKey}`,
          position: [relativeX, 0, relativeZ],
          rotationY,
          boardPosition: { x: centerX, y: centerY },
          isFast: building.id === BuildingType.FAST_BELT,
        });

        const itemsAtPosition = beltContentLayer[posKey] ?? [];
        if (itemsAtPosition.length === 0) {
          return;
        }

        itemsAtPosition.forEach(beltItem => {
          const itemConfig = ITEMS_BY_TYPE[beltItem.item];
          if (!itemConfig || !itemConfig.model) {
            return;
          }

          const alongOffset = (beltItem.progress - 0.5) * TILE_SPACING;
          const verticalOffset = getBeltItemVerticalOffset(beltItem.item);
          const stackCount = beltItem.count ?? 1;

          const offsetX = alongOffset * directionX;
          const offsetZ = alongOffset * directionZ;

          for (let stackIndex = 0; stackIndex < stackCount; stackIndex += 1) {
            const stackOffsetY =
              verticalOffset + stackIndex * BELT_ITEM_STACK_SPACING;

            beltItems.push({
              key: `belt-item-${posKey}-${beltItem.id}-${stackIndex}`,
              position: [
                relativeX + offsetX,
                stackOffsetY,
                relativeZ + offsetZ,
              ],
              rotationY,
              itemType: beltItem.item,
              boardPosition: { x: centerX, y: centerY },
            });
          }
        });
        return;
      }

      const buildingModelType = BUILDING_MODEL_TYPE_MAP[building.id];
      if (buildingModelType) {
        buildingModels.push({
          key: `building-${posKey}`,
          position: [relativeX, 0, relativeZ],
          rotationY: 0,
          type: buildingModelType,
          boardPosition: { x: centerX, y: centerY },
        });
      }
    });

    const targetPosition = targetPositionSnapshot;

    let previewFootprint: Position[] = [];
    let previewInvalid = false;

    if (quickBeltPreview && quickBeltPreview.segments.length > 0) {
      previewFootprint = quickBeltPreview.segments.map(segment => ({
        x: segment.position.x,
        y: segment.position.y,
      }));
      previewInvalid = !quickBeltPreview.isValid;
    } else if (buildMode && selectedBuilding) {
      if (buildingTargetSnapshot) {
        previewFootprint = getBuildingPositions(
          buildingTargetSnapshot.x,
          buildingTargetSnapshot.y,
          selectedBuilding
        );
      } else if (targetPosition) {
        previewFootprint = getBuildingPositions(
          targetPosition.x,
          targetPosition.y,
          selectedBuilding
        );
      }
    }

    const previewTiles: PreviewInstance[] = previewFootprint.map(pos => ({
      key: `preview-${pos.x},${pos.y}`,
      position: [
        (pos.x - playerPos.x) * TILE_SPACING,
        PREVIEW_OVERLAY_HEIGHT,
        (pos.y - playerPos.y) * TILE_SPACING,
      ] as [number, number, number],
      boardPosition: { x: pos.x, y: pos.y },
      isInvalid: previewInvalid,
    }));

    let buildingPreview: BuildingPreviewInstance | null = null;
    let assemblerPreview: AssemblerPreviewInstance | null = null;

    if (previewFootprint.length > 0 && selectedBuilding) {
      const firstPosition = previewFootprint[0];
      if (firstPosition) {
        let minX = firstPosition.x;
        let maxX = firstPosition.x;
        let minY = firstPosition.y;
        let maxY = firstPosition.y;

        previewFootprint.forEach(pos => {
          minX = Math.min(minX, pos.x);
          maxX = Math.max(maxX, pos.x);
          minY = Math.min(minY, pos.y);
          maxY = Math.max(maxY, pos.y);
        });

        const width = maxX - minX + 1;
        const height = maxY - minY + 1;
        const centerX = minX + Math.floor(width / 2);
        const centerY = minY + Math.floor(height / 2);

        const position: [number, number, number] = [
          (centerX - playerPos.x) * TILE_SPACING,
          0,
          (centerY - playerPos.y) * TILE_SPACING,
        ];

        if (selectedBuilding.id === BuildingType.ASSEMBLER) {
          assemblerPreview = {
            key: `preview-assembler-${centerX},${centerY}`,
            position,
          };
        } else {
          const previewModelType = BUILDING_MODEL_TYPE_MAP[selectedBuilding.id];
          if (previewModelType) {
            buildingPreview = {
              key: `preview-building-${centerX},${centerY}`,
              position,
              rotationY: 0,
              type: previewModelType,
            };
          }
        }
      }
    }

    const worldTargetPosition = targetPosition
      ? ([
          (targetPosition.x - playerPos.x) * TILE_SPACING,
          PREVIEW_OVERLAY_HEIGHT,
          (targetPosition.y - playerPos.y) * TILE_SPACING,
        ] as [number, number, number])
      : null;

    return {
      tiles,
      terrainOffset,
      previewTiles,
      buildingPreview,
      assemblerPreview,
      targetPosition: worldTargetPosition,
      normalizedZoom,
      assemblers,
      belts,
      beltItems,
      buildingModels,
      resourceDeposits,
      powerAlerts,
    };
  }, [
    buildMode,
    buildingLayer,
    beltContentLayer,
    buildingTargetSnapshot,
    playerPos,
    quickBeltPreview,
    selectedBuilding,
    targetPositionSnapshot,
    terrainSceneData,
  ]);

  const {
    tiles,
    terrainOffset,
    previewTiles,
    buildingPreview,
    assemblerPreview,
    targetPosition,
    normalizedZoom,
    assemblers,
    belts,
    beltItems,
    buildingModels,
    resourceDeposits,
    powerAlerts,
  } = sceneData;

  const hasLoadedModels = loadedGltfs !== null;
  const isLoadingModels = modelLoadingState.isLoading || !hasLoadedModels;
  const showModelDetails = modelLoadingState.isLoading;
  const showProgressBar = showModelDetails && modelLoadingState.itemsTotal > 0;
  const shouldRenderCanvas = hasEnteredScene && hasLoadedModels;
  const showStatusOverlay =
    !hasEnteredScene && (isLoadingModels || Boolean(modelLoadError));

  useEffect(() => {
    if (
      hasEnteredScene ||
      !hasLoadedModels ||
      modelLoadingState.isLoading ||
      modelLoadError
    ) {
      return;
    }

    handleEnterScene();
  }, [
    handleEnterScene,
    hasEnteredScene,
    hasLoadedModels,
    modelLoadError,
    modelLoadingState.isLoading,
  ]);

  const statusClassNames = ['three-renderer__status'];
  if (isLoadingModels && !modelLoadError) {
    statusClassNames.push('three-renderer__status--loading');
  }
  if (modelLoadError) {
    statusClassNames.push(
      'three-renderer__status--error',
      'three-renderer__status--interactive'
    );
  }

  return (
    <div className='game-container'>
      <div
        className='three-renderer'
        ref={containerRef}
        onMouseLeave={handleCanvasPointerLeave}
      >
        {shouldRenderCanvas && loadedGltfs && (
          <Canvas
            shadows
            camera={{
              fov: 50,
              near: 0.1,
              far: 5000,
              position: [0, 24, 24],
              up: [0, 1, 0],
            }}
            gl={{ antialias: true, alpha: true }}
            dpr={[1, 2]}
            onCreated={() => setIsCanvasReady(true)}
          >
            <SceneContent
              tiles={tiles}
              terrainOffset={terrainOffset}
              previewTiles={previewTiles}
              buildingPreview={buildingPreview}
              assemblerPreview={assemblerPreview}
              targetPosition={targetPosition}
              normalizedZoom={normalizedZoom}
              playerFacing={playerFacing}
              playerPos={playerPos}
              assemblers={assemblers}
              belts={belts}
              beltItems={beltItems}
              buildingModels={buildingModels}
              resourceDeposits={resourceDeposits}
              powerAlerts={powerAlerts}
              powerOverlayActive={powerOverlayActive}
              loadedGltfs={loadedGltfs}
              onBoardPointerDown={handleBoardPointerDown}
              onBoardPointerUp={handleBoardPointerUp}
              onBoardPointerOver={handleBoardPointerOver}
              onBoardPointerOut={handleBoardPointerOut}
            />
            <StatsBridge stats={stats} />
          </Canvas>
        )}

        {showStatusOverlay && (
          <div
            className={statusClassNames.join(' ')}
            role='status'
            aria-live='polite'
          >
            {isLoadingModels && !modelLoadError && (
              <>
                <div className='three-renderer__poster-wrapper'>
                  <img
                    src='/factorygpt-poster-scaled.png'
                    srcSet='/factorygpt-poster-scaled@2x.png 2x'
                    width={260}
                    height={390}
                    alt='FactoryGPT key art featuring a neon factory skyline'
                    className='three-renderer__poster'
                  />
                </div>
                <div className='three-renderer__status-text'>
                  <div className='three-renderer__status-line'>
                    Loading the 3D renderer…
                  </div>
                  {showModelDetails && (
                    <>
                      <div className='three-renderer__status-line'>
                        Loading model:{' '}
                        {modelLoadingState.activeLabel ?? 'Assets'}
                      </div>
                      {showProgressBar && (
                        <>
                          <div className='three-renderer__status-line three-renderer__status-line--sub'>
                            {modelLoadingState.itemsLoaded} /{' '}
                            {modelLoadingState.itemsTotal} models ready
                          </div>
                          <div
                            className='three-renderer__progress'
                            role='progressbar'
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={Math.round(
                              modelLoadingState.progressPercentage
                            )}
                          >
                            <div
                              className='three-renderer__progress-bar'
                              style={{
                                width: `${modelLoadingState.progressPercentage}%`,
                              }}
                            />
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </>
            )}

            {modelLoadError && (
              <div className='three-renderer__status-text'>
                <div className='three-renderer__status-line'>
                  {modelLoadError}
                </div>
                <button
                  type='button'
                  className='three-renderer__start-button'
                  onClick={handleRetryModelLoad}
                >
                  Retry Loading
                </button>
              </div>
            )}
          </div>
        )}

        {isCanvasReady && (
          <div className='three-renderer__hint'>
            3D view — toggle back to ASCII from the top menu.
          </div>
        )}
      </div>
    </div>
  );
};
